import {
    OauthAudience,
    OauthConnection,
    OauthConsentDecision,
    OauthConsentRequest,
    OauthOrganizationConnection,
    OauthPolicySettings,
    TaggingBillingTerms,
    TaggingCollectionSetup,
} from "@/types/generated/management-api-types";
import {
    createApi,
    EndpointBuilder,
    FetchBaseQueryMeta,
} from "@reduxjs/toolkit/query/react";
import { paths } from "@/types/generated/api-schema";
import { getBaseQuery } from "@/services/apis/baseQuery.ts";
import merge from "lodash.merge";
import { FunnelConfig } from "@/features/Funnel/types";
import { SavedReportConfig } from "@/features/Reporting/ReportingClassic/types";
import { ImportDataRequest } from "@/features/Debugger/CsvImport/types";
import { assertNotUndefined } from "@/util/typeguards.ts";
import { AudienceConfig } from "@/features/Audience/types.ts";
import { icpProfileOf } from "@/features/IcpFit/icp.ts";
import { dataApi } from "@/services/apis/data.ts";
import {
    IntegrationGrant,
    ChatConversation,
    ChatConversationDetail,
    CheckAction,
    CheckCatalogEntry,
    CheckResult,
    CheckResultStatus,
    CheckSummaryResponse,
    CheckWithheldResponse,
    MaturityResponse,
    FeedbackRating,
    Ga4DataStream,
    GoogleTagManagerContainerConfig,
    GoogleTagManagerContainerGa4,
    GoogleTagManagerContainers,
    OrganizationInvoiceList,
    TaggingContainerDetails,
    TaggingContainerTagManagerSetup,
    TeamInvitation,
    TrackingPlan,
    TrackingPlanChangeSets,
    TrackingPlanEvent,
    TrackingPlanImplementationPlan,
    TrackingPlanKnownVendor,
    PolicyRuleKey,
    RuleLevel,
    SnapshotEntityKind,
    TagManagerChangeSetDetail,
    TrackingPlanStatusReport,
    TrackingPlanWorkspaceSummary,
    ClientManagerWorkspaceHealth,
    TrackingPlanTagManager,
    TrackingPlanTimeline,
    TrackingPlanVersion,
    TrackingPlanVersionDiff,
    Workspace,
} from "@/types/generated/management-api-types";
import { TrackingPlanEventDraft } from "@/features/TrackingPlan/types.ts";
import {
    TaggingContainerBlockerRecoveryUpdate,
    TaggingContainerCreate,
    TaggingContainerDnsRecordsEmail,
    TaggingContainerMeasurementIdSave,
    TaggingContainerUpdate,
} from "@/features/Workspace/Settings/ServerSideTagging/types.ts";
import { setSelectedWorkspace } from "@/features/Workspace/Workspace.slice.ts";
import { toast } from "react-hot-toast";
import { ApiError, apiErrorFromException } from "@/services/apis/apiError.ts";
import type { ChatAllowanceRead } from "@/features/Chat/allowance.ts";
import type { IcpProfile } from "@/types/generated/IcpProfile.ts";
import { fetchChatAllowance } from "@/features/Chat/chatApi.ts";

type TagType =
    | "Workspaces"
    | "TeamMembers"
    | "Invitations"
    | "User"
    | "WorkspaceIntegrations"
    | "AdNetworkEntities"
    | "AudienceSyncs"
    | "Funnels"
    | "LensDetails"
    | "Automations"
    | "Audiences"
    | "Reports"
    | "ConversionSyncs"
    | "Checks"
    | "ChatConversations"
    | "ChatAllowance"
    | "TaggingContainer"
    | "TrackingPlan"
    | "TrackingPlanVersions"
    | "TrackingPlanTimeline"
    | "TagManagerChangeSets"
    | "OrganizationUsage"
    | "OauthConnections"
    | "OauthPolicy"
    | "IcpProfile";

/**
 * Request bodies for the Icp profile, written as the app holds them. The base
 * query snake_cases them on the way out, the rules (data-api's IcpProfile
 * shape) included; the Api puts the rules' keys back before storing them.
 */
type IcpProfileSaveBody = {
    rules: IcpProfile;
    description: string | null;
};
type IcpProfileBacktestBody = {
    rules: IcpProfile;
    /** False asks the rules' questions of nobody: the test reads what companies have stored. */
    ask?: boolean;
};

/** How often the Icp draft is asked for while it is being made, and for how long. */
const ICP_POLL_MS = 2_000;
const ICP_POLL_TIMEOUT_MS = 180_000;

/**
 * Posts to a route that works on a queue and answers 202 until it is done,
 * again every few seconds, so a mutation stays one call to its caller:
 * loading until there is an answer or an error.
 */
async function postUntilReady<Result, Failure>(
    baseQuery: (arg: {
        url: string;
        method: string;
        body: unknown;
    }) => PromiseLike<{ data?: unknown; error?: Failure; meta?: unknown }>,
    url: string,
    body: unknown,
    timedOut: Failure,
): Promise<{ data: Result } | { error: Failure }> {
    const deadline = Date.now() + ICP_POLL_TIMEOUT_MS;
    for (;;) {
        const result = await baseQuery({ url, method: "POST", body });
        if (result.error) {
            return { error: result.error };
        }
        const status = (result.meta as FetchBaseQueryMeta | undefined)?.response
            ?.status;
        if (status !== 202) {
            return { data: result.data as Result };
        }
        if (Date.now() > deadline) {
            return { error: timedOut };
        }
        await new Promise((resolve) => setTimeout(resolve, ICP_POLL_MS));
    }
}

type ApiEndpointBuilder = EndpointBuilder<
    ReturnType<typeof getBaseQuery>,
    TagType,
    "api"
>;

const tagTypes: TagType[] = [
    "Workspaces",
    "TeamMembers",
    "Invitations",
    "AudienceSyncs",
    "WorkspaceIntegrations",
    "AdNetworkEntities",
    "Funnels",
    "LensDetails",
    "Automations",
    "Audiences",
    "Reports",
    "ConversionSyncs",
    "Checks",
    "ChatConversations",
    "ChatAllowance",
    "TaggingContainer",
    "TrackingPlan",
    "TrackingPlanVersions",
    "TrackingPlanTimeline",
    "TagManagerChangeSets",
    "OrganizationUsage",
    "OauthConnections",
    "OauthPolicy",
    "IcpProfile",
];

// Define a service using a base URL and expected endpoints
export const api = createApi({
    reducerPath: "api",
    tagTypes,
    baseQuery: getBaseQuery(import.meta.env.VITE_MANAGEMENT_API_URL),
    endpoints: (builder) => ({
        ...getUserPaths(builder),
        ...getTeamMemberPaths(builder),
        ...getSubscriptionPaths(builder),
        ...getLensPaths(builder),
        ...getIcpPaths(builder),
        ...getTaggingContainerPaths(builder),
        ...getUsagePaths(builder),
        ...getInvoicePaths(builder),
        ...getWorkspacePaths(builder),
        ...getWorkspaceIntegrationPaths(builder),
        ...getFunnelPaths(builder),
        ...getAudiencePaths(builder),
        ...getAudienceSyncPaths(builder),
        ...getConversionSyncPaths(builder),
        ...getClientManagerPaths(builder),
        ...getAutomationPaths(builder),
        ...getReportPaths(builder),
        ...getCheckPaths(builder),
        ...getChatConversationPaths(builder),
        ...getTrackingPlanPaths(builder),
        ...getOauthPaths(builder),
    }),
});

function getUserPaths(builder: ApiEndpointBuilder) {
    return {
        // Once per sign-in, before any other request: where a first sign-in
        // becomes an account, and where Cognito's profile reaches it.
        startSession: builder.mutation<void, { idToken: string }>({
            query: (data) => ({
                url: `auth/session`,
                method: "POST",
                body: data,
            }),
            // Another account has the address (409), or single sign-on has a
            // say (403): ServerSession shows its own screen for each, so no
            // toast on top of it
            extraOptions: { expectedStatuses: [409, 403] },
            invalidatesTags: [{ type: "User", id: "LIST" }],
        }),
        getUser: builder.query<
            paths["/user"]["get"]["responses"]["200"]["content"]["application/json"],
            undefined
        >({
            query: () => ({
                url: `user`,
            }),
            providesTags: [{ type: "User", id: "LIST" }],
        }),
        updateUser: builder.mutation<
            paths["/user"]["patch"]["responses"]["200"]["content"]["application/json"],
            Partial<
                paths["/user"]["patch"]["requestBody"]["content"]["application/json"]
            >
        >({
            query: (data) => ({
                url: `user`,
                method: "PATCH",
                body: data,
            }),
            async onQueryStarted(data, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    api.util.updateQueryData("getUser", undefined, (draft) => {
                        Object.assign(draft, data);
                    }),
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
        }),
        /**
         * The dashboard layouts alone, without `updateUser`'s optimistic
         * write: that rebuilds the user model and re-renders everything
         * under it on every keystroke of the configurator. The cache learns
         * the new list once the Api has it, so nothing refetches.
         */
        updateDashboardLayouts: builder.mutation<
            paths["/user"]["patch"]["responses"]["200"]["content"]["application/json"],
            NonNullable<
                paths["/user"]["patch"]["requestBody"]["content"]["application/json"]["dashboardLayouts"]
            >
        >({
            query: (dashboardLayouts) => ({
                url: `user`,
                method: "PATCH",
                body: { dashboardLayouts },
            }),
            async onQueryStarted(
                dashboardLayouts,
                { dispatch, queryFulfilled },
            ) {
                await queryFulfilled;
                dispatch(
                    api.util.updateQueryData("getUser", undefined, (draft) => {
                        draft.dashboardLayouts = dashboardLayouts;
                    }),
                );
            },
        }),
        deleteUser: builder.mutation({
            query() {
                return {
                    url: `user`,
                    method: "DELETE",
                };
            },
        }),
        dismissDialog: builder.mutation<
            paths["/user/dismissed-dialogs"]["post"]["responses"]["204"],
            paths["/user/dismissed-dialogs"]["post"]["requestBody"]["content"]["application/json"]
        >({
            query: (data) => {
                return {
                    url: `user/dismissed-dialogs`,
                    method: "POST",
                    body: data,
                };
            },
            async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    api.util.updateQueryData("getUser", undefined, (draft) => {
                        draft.dismissedDialogs.push(id);
                    }),
                );

                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
        }),
        updateOrganization: builder.mutation<
            paths["/user/organization"]["patch"]["responses"]["200"],
            Partial<
                paths["/user/organization"]["patch"]["requestBody"]["content"]["application/json"]
            > & { id: string }
        >({
            query: ({ id: _id, ...data }) => {
                return {
                    url: `user/organization`,
                    method: "PATCH",
                    body: data,
                };
            },
            async onQueryStarted(
                { id, ...orgData },
                { dispatch, queryFulfilled },
            ) {
                const userPatchResult = dispatch(
                    api.util.updateQueryData("getUser", undefined, (draft) => {
                        merge(draft.currentOrganization, orgData);
                    }),
                );

                const workspacePatchResult = dispatch(
                    api.util.updateQueryData(
                        "getWorkspaces",
                        undefined,
                        (draft) => {
                            draft.forEach((ws) => {
                                if (!!ws && ws.organization.id === id) {
                                    merge(ws.organization, orgData);
                                }
                            });
                        },
                    ),
                );

                try {
                    await queryFulfilled;
                } catch {
                    userPatchResult.undo();
                    workspacePatchResult.undo();
                }
            },
        }),
        acceptTerms: builder.mutation<
            paths["/organization/accept-terms"]["post"]["responses"]["200"]["content"]["application/json"],
            paths["/organization/accept-terms"]["post"]["requestBody"]["content"]["application/json"]
        >({
            query: (data) => {
                return {
                    url: `organization/accept-terms`,
                    method: "POST",
                    body: data,
                };
            },
            async onQueryStarted({ version }, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    api.util.updateQueryData("getUser", undefined, (draft) => {
                        draft.currentOrganization.acceptedTermsVersion =
                            version;
                    }),
                );

                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
        }),
    };
}

function getWorkspacePaths(builder: ApiEndpointBuilder) {
    return {
        getWorkspaces: builder.query<
            paths["/workspaces"]["get"]["responses"]["200"]["content"]["application/json"],
            undefined
        >({
            query: () => ({
                url: `workspaces`,
                method: "GET",
            }),
            providesTags: (result) =>
                result
                    ? [
                          ...result.map(
                              ({ name }) =>
                                  ({
                                      type: "Workspaces",
                                      name,
                                  }) as const,
                          ),
                          { type: "Workspaces", id: "LIST" },
                      ]
                    : [{ type: "Workspaces", id: "LIST" }],
        }),
        createWorkspace: builder.mutation<
            paths["/workspaces"]["post"]["responses"]["201"]["content"]["application/json"],
            paths["/workspaces"]["post"]["requestBody"]["content"]["application/json"] & {
                managedOrganizationId?: string;
            }
        >({
            query({ managedOrganizationId, ...data }) {
                return {
                    url: `workspaces`,
                    method: "POST",
                    body: data,
                    managedOrganizationId,
                };
            },
            invalidatesTags: [{ type: "Workspaces", id: "LIST" }],
        }),
        updateWorkspace: builder.mutation<
            paths["/workspaces/{id}"]["patch"]["responses"]["200"]["content"]["application/json"],
            Partial<
                paths["/workspaces/{id}"]["patch"]["requestBody"]["content"]["application/json"]
            > & // @TODO Needed to make this partial because of a regression in NelmioApiDocBundle
                // @see: https://github.com/nelmio/NelmioApiDocBundle/pull/2103
                paths["/workspaces/{id}"]["patch"]["parameters"]["path"]
        >({
            query: ({ id, ...data }) => ({
                url: `workspaces/${id}`,
                method: "PATCH",
                body: data,
            }),
            async onQueryStarted(
                { id, ...data },
                { dispatch, queryFulfilled },
            ) {
                const patchResult = dispatch(
                    api.util.updateQueryData(
                        "getWorkspaces",
                        undefined,
                        (draft) => {
                            draft.forEach((ws) => {
                                if (!!ws && ws.id === id) {
                                    merge(ws, data);
                                    if (Array.isArray(data.cutOffEvents)) {
                                        ws.cutOffEvents = data.cutOffEvents;
                                    }

                                    if (data.reportingCurrency === false) {
                                        ws.reportingCurrency = null;
                                    }
                                }
                            });
                        },
                    ),
                );

                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
        }),
        // Lens contacts have their own endpoint, not a field on the workspace
        // patch: switching them on is accepting Chapter 10 of the product
        // terms, which the server records and only an organization admin may
        // do. Answers with the workspace, which Workspace.slice.ts takes as
        // the selected one.
        updateLensContacts: builder.mutation<
            paths["/workspaces/{id}/lens-contacts"]["put"]["responses"]["200"]["content"]["application/json"],
            paths["/workspaces/{id}/lens-contacts"]["put"]["requestBody"]["content"]["application/json"] &
                paths["/workspaces/{id}/lens-contacts"]["put"]["parameters"]["path"]
        >({
            query: ({ id, ...data }) => ({
                url: `workspaces/${id}/lens-contacts`,
                method: "PUT",
                body: data,
            }),
            async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
                const { data: workspace } = await queryFulfilled;
                dispatch(
                    api.util.updateQueryData(
                        "getWorkspaces",
                        undefined,
                        (draft) => {
                            draft.forEach((ws) => {
                                if (!!ws && ws.id === id) {
                                    ws.lensContactsEnabled =
                                        workspace.lensContactsEnabled;
                                }
                            });
                        },
                    ),
                );
            },
        }),
        deleteWorkspace: builder.mutation<
            paths["/workspaces/{id}"]["delete"]["responses"]["204"],
            paths["/workspaces/{id}"]["delete"]["parameters"]["path"]
        >({
            query({ id }) {
                return {
                    url: `workspaces/${id}`,
                    method: "DELETE",
                };
            },
            async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
                await queryFulfilled;
                dispatch(setSelectedWorkspace(undefined));
                dispatch(
                    api.util.updateQueryData(
                        "getWorkspaces",
                        undefined,
                        (draft) => {
                            const index = draft.findIndex((ws) => ws.id === id);
                            if (index >= 0) {
                                draft.splice(index, 1);
                            }
                        },
                    ),
                );
                toast.success("Workspace deleted 👋");
            },
        }),
        rotateWebhookSigningSecret: builder.mutation<
            paths["/workspaces/{id}/webhook-signing-secret"]["delete"]["responses"]["200"]["content"]["application/json"],
            paths["/workspaces/{id}/webhook-signing-secret"]["delete"]["parameters"]["path"]
        >({
            query({ id }) {
                return {
                    url: `workspaces/${id}/webhook-signing-secret`,
                    method: "DELETE",
                };
            },
            async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(
                        api.util.updateQueryData(
                            "getWorkspaces",
                            undefined,
                            (draft) => {
                                const workspace = draft.find(
                                    (ws) => ws.id === id,
                                );
                                if (workspace) {
                                    workspace.webhookSigningSecret =
                                        data.webhookSigningSecret;
                                }
                            },
                        ),
                    );
                } catch {
                    // Error handled by the component
                }
            },
        }),
    };
}

function getTeamMemberPaths(builder: ApiEndpointBuilder) {
    return {
        getAllTeamMembers: builder.query<
            paths["/team-members"]["get"]["responses"]["200"]["content"]["application/json"],
            void
        >({
            query: () => ({
                url: `team-members`,
            }),
            providesTags: (result) =>
                result
                    ? [
                          ...result.map(
                              ({ id }) =>
                                  ({
                                      type: "TeamMembers",
                                      id,
                                  }) as const,
                          ),
                          { type: "TeamMembers", id: "LIST" },
                      ]
                    : [{ type: "TeamMembers", id: "LIST" }],
        }),
        getInvitations: builder.query<TeamInvitation[], void>({
            query: () => ({
                url: `invitations`,
            }),
            providesTags: [{ type: "Invitations", id: "LIST" }],
        }),
        inviteTeamMember: builder.mutation<
            TeamInvitation,
            paths["/invitations"]["post"]["requestBody"]["content"]["application/json"]
        >({
            query(data) {
                return {
                    url: `invitations`,
                    method: "POST",
                    body: data,
                };
            },
            invalidatesTags: [{ type: "Invitations", id: "LIST" }],
        }),
        updateTeamMember: builder.mutation<
            paths["/team-members/{id}"]["patch"]["responses"]["200"]["content"]["application/json"],
            paths["/team-members/{id}"]["patch"]["requestBody"]["content"]["application/json"] &
                paths["/team-members/{id}"]["patch"]["parameters"]["path"]
        >({
            query({ id, ...data }) {
                return {
                    url: `team-members/${id}`,
                    method: "PATCH",
                    body: data,
                };
            },
            async onQueryStarted(
                { id, permissionsType, active },
                { dispatch, queryFulfilled },
            ) {
                const patchResult = dispatch(
                    api.util.updateQueryData(
                        "getAllTeamMembers",
                        undefined,
                        (draft) => {
                            draft.forEach((tm) => {
                                if (!!tm && tm.id === id) {
                                    if (
                                        typeof permissionsType !== "undefined"
                                    ) {
                                        tm.currentUserPermissionsType =
                                            permissionsType;
                                    }

                                    if (typeof active !== "undefined") {
                                        tm.currentUserActiveInOrganization =
                                            active;
                                    }
                                }
                            });
                        },
                    ),
                );

                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
        }),
        resendInvitation: builder.mutation<void, { id: string }>({
            query: ({ id }) => ({
                url: `invitations/${id}/resend`,
                method: "POST",
            }),
            invalidatesTags: [{ type: "Invitations", id: "LIST" }],
        }),
        revokeInvitation: builder.mutation<void, { id: string }>({
            query: ({ id }) => ({
                url: `invitations/${id}`,
                method: "DELETE",
            }),
            invalidatesTags: [{ type: "Invitations", id: "LIST" }],
        }),
        deleteTeamMember: builder.mutation<
            paths["/team-members/{id}"]["delete"]["responses"]["204"],
            paths["/team-members/{id}"]["delete"]["parameters"]["path"]
        >({
            query: ({ id }) => ({
                url: `team-members/${id}`,
                method: "DELETE",
            }),
            async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    api.util.updateQueryData(
                        "getAllTeamMembers",
                        undefined,
                        (draft) => {
                            const index = draft.findIndex((tm) => tm.id === id);
                            if (index >= 0) {
                                draft.splice(index, 1);
                            }
                        },
                    ),
                );

                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
            invalidatesTags: [{ type: "TeamMembers", id: "LIST" }],
        }),
    };
}

function getSubscriptionPaths(builder: ApiEndpointBuilder) {
    return {
        getCheckoutSetupSession: builder.mutation<
            paths["/user/organization/subscription/checkout-session-setup"]["post"]["responses"]["200"]["content"]["application/json"],
            undefined
        >({
            query: () => ({
                url: `user/organization/subscription/checkout-session-setup`,
                method: "POST",
            }),
        }),
        getSelfServePlans: builder.query<
            paths["/user/organization/subscription/plans"]["get"]["responses"]["200"]["content"]["application/json"],
            void
        >({
            query: () => ({
                url: "user/organization/subscription/plans",
            }),
        }),
        startSubscriptionCheckoutSession: builder.mutation<
            paths["/user/organization/subscription/checkout-session"]["post"]["responses"]["200"]["content"]["application/json"],
            paths["/user/organization/subscription/checkout-session"]["post"]["requestBody"]["content"]["application/json"]
        >({
            query: (data) => ({
                url: "user/organization/subscription/checkout-session",
                method: "POST",
                body: data,
            }),
        }),
        getUpgradeSubscriptionPortalSession: builder.mutation<
            paths["/user/organization/subscription/customer-portal-session-upgrade"]["post"]["responses"]["200"]["content"]["application/json"],
            paths["/user/organization/subscription/customer-portal-session-upgrade"]["post"]["requestBody"]["content"]["application/json"]
        >({
            query: (data) => ({
                url: "user/organization/subscription/customer-portal-session-upgrade",
                method: "POST",
                body: data,
            }),
        }),
        getManageSubscriptionPortalSession: builder.mutation<
            paths["/user/organization/subscription/customer-portal-session-manage"]["post"]["responses"]["200"]["content"]["application/json"],
            undefined
        >({
            query: (data) => ({
                url: "user/organization/subscription/customer-portal-session-manage",
                method: "POST",
                body: data,
            }),
        }),
        changeLensPlan: builder.mutation<
            paths["/user/organization/subscription/lens"]["post"]["responses"]["204"],
            paths["/user/organization/subscription/lens"]["post"]["requestBody"]["content"]["application/json"]
        >({
            query: (data) => ({
                url: "user/organization/subscription/lens",
                method: "POST",
                body: data,
            }),
            invalidatesTags: [
                { type: "User", id: "LIST" },
                { type: "LensDetails", id: "LIST" },
                // A plan change grants credits and moves the overage rate.
                { type: "OrganizationUsage", id: "LIST" },
            ],
        }),
        cancelLensPlan: builder.mutation<
            paths["/user/organization/subscription/lens"]["delete"]["responses"]["204"],
            undefined
        >({
            query: () => ({
                url: "user/organization/subscription/lens",
                method: "DELETE",
            }),
            invalidatesTags: [{ type: "User", id: "LIST" }],
        }),
        // What a tier is billed in and how often: the subscription's own
        // currency and cadence, which no tier purchase changes.
        getTaggingBillingTerms: builder.query<TaggingBillingTerms, void>({
            query: () => ({
                url: "user/organization/subscription/tagging/billing-terms",
            }),
        }),
        // Buys a tier or moves between them. An upgrade happens now; a
        // downgrade waits for the end of the paid period, and the user
        // payload says so until then (taggingPlanChangeAt).
        changeTaggingPlan: builder.mutation<
            paths["/user/organization/subscription/tagging"]["post"]["responses"]["204"],
            paths["/user/organization/subscription/tagging"]["post"]["requestBody"]["content"]["application/json"]
        >({
            query: (data) => ({
                url: "user/organization/subscription/tagging",
                method: "POST",
                body: data,
            }),
            invalidatesTags: [
                { type: "User", id: "LIST" },
                { type: "OrganizationUsage", id: "LIST" },
            ],
        }),
        cancelTaggingPlan: builder.mutation<
            paths["/user/organization/subscription/tagging"]["delete"]["responses"]["204"],
            undefined
        >({
            query: () => ({
                url: "user/organization/subscription/tagging",
                method: "DELETE",
            }),
            invalidatesTags: [{ type: "User", id: "LIST" }],
        }),
        keepTaggingPlan: builder.mutation<
            paths["/user/organization/subscription/tagging/scheduled-change"]["delete"]["responses"]["204"],
            undefined
        >({
            query: () => ({
                url: "user/organization/subscription/tagging/scheduled-change",
                method: "DELETE",
            }),
            invalidatesTags: [{ type: "User", id: "LIST" }],
        }),
    };
}

// Server-side tracking (SPE-1171). Every action is super-admin-only on the Api; the tab
// that calls them is behind the `server-side-tracking` flag.
function getTaggingContainerPaths(builder: ApiEndpointBuilder) {
    return {
        getTaggingContainer: builder.query<TaggingContainerDetails, undefined>({
            query: () => ({
                url: "tagging-container",
                method: "GET",
            }),
            // A workspace with no container is the common case for this tab, and it is
            // what the provision form renders. Without this the tab pops a red toast on
            // arrival, and again after a successful tear-down, when the refetch that the
            // invalidated tag triggers gets the same 404 — a completed action reading as
            // a failure.
            extraOptions: { expectedStatuses: [404] },
            providesTags: [{ type: "TaggingContainer", id: "CURRENT" }],
        }),
        createTaggingContainer: builder.mutation<
            TaggingContainerDetails,
            TaggingContainerCreate
        >({
            extraOptions: { errorToast: false },
            query: (body) => ({
                url: "tagging-container",
                method: "POST",
                body,
            }),
            invalidatesTags: [
                { type: "TaggingContainer", id: "CURRENT" },
                { type: "TaggingContainer", id: "TAG_MANAGER_SETUP" },
                { type: "TrackingPlan", id: "SETUP" },
                // Choosing the pair binds a waiting tracking plan to it.
                { type: "TrackingPlan", id: "PLAN" },
                { type: "TrackingPlan", id: "STATUS" },
                { type: "TagManagerChangeSets", id: "LIST" },
            ],
        }),
        /**
         * Local to the Api: nothing is provisioned. The Tag Manager check compares the web
         * container against the id, and the tracking plan renders with it, so both are
         * invalidated with the container.
         */
        saveTaggingContainerMeasurementId: builder.mutation<
            void,
            TaggingContainerMeasurementIdSave
        >({
            extraOptions: { errorToast: false },
            query: (body) => ({
                url: "tagging-container/measurement-id",
                method: "PUT",
                body,
            }),
            invalidatesTags: [
                { type: "TaggingContainer", id: "CURRENT" },
                { type: "TaggingContainer", id: "TAG_MANAGER_SETUP" },
                { type: "TrackingPlan", id: "SETUP" },
                { type: "TagManagerChangeSets", id: "LIST" },
            ],
        }),
        /**
         * Local to the Api, like the measurement id: the control plane is never called,
         * and turning it off leaves the wrapper serving. Only which snippet the tab hands
         * out changes, and the report reads it to know whether there is anything to show.
         */
        saveTaggingContainerBlockerRecovery: builder.mutation<
            void,
            TaggingContainerBlockerRecoveryUpdate
        >({
            extraOptions: { errorToast: false },
            query: (body) => ({
                url: "tagging-container/blocker-recovery",
                method: "PUT",
                body,
            }),
            invalidatesTags: [{ type: "TaggingContainer", id: "CURRENT" }],
        }),
        updateTaggingContainer: builder.mutation<
            TaggingContainerDetails,
            TaggingContainerUpdate
        >({
            query: (body) => ({
                url: "tagging-container",
                method: "PATCH",
                body,
            }),
            invalidatesTags: [
                { type: "TaggingContainer", id: "CURRENT" },
                { type: "TaggingContainer", id: "TAG_MANAGER_SETUP" },
            ],
        }),
        /**
         * Every container the WORKSPACE may choose from, across all of its Google Tag
         * Manager connections. Each connection is scoped to the one Tag Manager account
         * somebody picked for this workspace, so this is not everything the connected
         * Google users can see — a client whose containers span two accounts connects
         * twice, and the Api unions them. Each container names the connection it came
         * through, which is what reading its config needs.
         */
        getWorkspaceGoogleTagManagerContainers: builder.query<
            GoogleTagManagerContainers,
            { refresh: number }
        >({
            query: ({ refresh }) => ({
                url: "google-tag-manager/containers",
                method: "GET",
                params: refresh > 0 ? { refresh: "true" } : undefined,
            }),
            // A quota hit is a 429 the form renders inline, as a Notice with the Api's
            // own sentence in it. Without this the base query would toast it as well —
            // the same message twice — and, because the wait is cached, again on every
            // remount until it lapses.
            extraOptions: { expectedStatuses: [429] },
        }),
        /**
         * The Ga4 properties the chosen web container already sends to, so the setup
         * form can fill the measurement id in instead of asking somebody to copy it out
         * of the Ga4 admin. One container's worth, never the account's: the same Google
         * connection can often see every property an agency runs, and the person on this
         * screen may be the agency's client.
         */
        getGoogleTagManagerContainerGa4: builder.query<
            GoogleTagManagerContainerGa4,
            { id: string; publicId: string }
        >({
            query: ({ id, publicId }) => ({
                url: `workspace-integrations/${id}/google-tag-manager/containers/${publicId}/ga4`,
                method: "GET",
            }),
            // A container the connected account cannot see answers 404, and a container
            // that has never been published has nothing to read: both leave the field to
            // be typed, which the form says where the user is looking.
            extraOptions: { expectedStatuses: [400, 404, 429] },
        }),
        /**
         * The Ga4 streams that measure this workspace's own website, for when the
         * chosen container holds no measurement id we can read — a container being set
         * up now, or one that keeps the id somewhere only the browser can resolve.
         *
         * Scoped to the domain server-side, so an agency's client is never offered the
         * rest of the agency's estate. Empty is the ordinary case, not an error: the
         * field then asks for the id.
         */
        getWorkspaceGa4DataStreams: builder.query<Ga4DataStream[], void>({
            query: () => ({
                url: "google-analytics/data-streams",
                method: "GET",
            }),
        }),
        /**
         * The chosen server container's config string, read from Tag Manager so it
         * need not be found in the Ui and pasted. Not kept on either side: it carries
         * the container's auth secret, and it is asked for once, when the container is
         * picked.
         */
        getGoogleTagManagerContainerConfig: builder.query<
            GoogleTagManagerContainerConfig,
            { id: string; publicId: string }
        >({
            query: ({ id, publicId }) => ({
                url: `workspace-integrations/${id}/google-tag-manager/containers/${publicId}/config`,
                method: "GET",
            }),
            // A container the connected account cannot see answers 404, and the form
            // handles that where the user is looking: the config field appears with a
            // description saying where to copy from. A toast on top would be the same
            // news, louder and further away.
            extraOptions: { expectedStatuses: [404, 429] },
            keepUnusedDataFor: 0,
        }),
        deleteTaggingContainer: builder.mutation<void, undefined>({
            query: () => ({
                url: "tagging-container",
                method: "DELETE",
            }),
            invalidatesTags: [
                { type: "TaggingContainer", id: "CURRENT" },
                { type: "TaggingContainer", id: "TAG_MANAGER_SETUP" },
            ],
        }),
        /**
         * Email the container's Dns records to an address, for whoever runs the
         * customer's Dns. The Api composes the email from the container and the
         * caller's verified address; nothing in the request but the recipient reaches
         * it. The dialog renders request errors inline, including the server's reason
         * when records are not ready, the address is invalid, or the rate limit is hit.
         */
        emailTaggingContainerDnsRecords: builder.mutation<
            void,
            TaggingContainerDnsRecordsEmail
        >({
            query: (body) => ({
                url: "tagging-container/dns-records/email",
                method: "POST",
                body,
            }),
            extraOptions: { errorToast: false },
            // A 404 here is the Api saying the tenant is gone and the row with it — the
            // same answer GET gives — so the container is re-read and the panel falls
            // back to the form instead of keeping a container that no longer exists.
            invalidatesTags: (_result, error) =>
                error?.status === 404
                    ? [{ type: "TaggingContainer", id: "CURRENT" }]
                    : [],
        }),
        /**
         * The Api answers from a ten-minute cache, because every read costs ~5 Tag
         * Manager Api calls against a quota shared by every customer. `refresh` is a
         * counter rather than a boolean: 0 reads the cache, anything above it asks the
         * Api to read Tag Manager again — and each increment is a new query arg, so the
         * button re-fetches instead of RTK Query handing back its own cached answer.
         */
        getTaggingContainerTagManagerSetup: builder.query<
            TaggingContainerTagManagerSetup,
            { refresh: number }
        >({
            query: ({ refresh }) => ({
                url: "tagging-container/tag-manager-setup",
                method: "GET",
                params: refresh > 0 ? { refresh: "true" } : undefined,
            }),
            // Invalidated by every write to the container: the checks are about a
            // specific container's recorded ids, and a workspace that removed one and
            // set up another must not be shown the previous one's answers.
            providesTags: [
                { type: "TaggingContainer", id: "TAG_MANAGER_SETUP" },
            ],
        }),
    };
}

/**
 * The ideal customer profile (specs/icp-fit/plan.md §3). Saving or removing
 * it changes the workspace payload (which carries the rules to data-api) and
 * every company's fit, so both caches go.
 */
function getIcpPaths(builder: ApiEndpointBuilder) {
    return {
        getIcpProfile: builder.query<
            paths["/icp-profile"]["get"]["responses"]["200"]["content"]["application/json"],
            undefined
        >({
            // "No profile yet" is a state of the screen, not an error.
            extraOptions: { expectedStatuses: [404] },
            query: () => ({ url: "icp-profile", method: "GET" }),
            providesTags: [{ type: "IcpProfile", id: "CURRENT" }],
        }),
        // How far the organization's Icp audit is. Polled by its screen
        // while it runs, so it carries no tag.
        getIcpAudit: builder.query<
            paths["/icp-audit"]["get"]["responses"]["200"]["content"]["application/json"],
            undefined
        >({
            query: () => ({ url: "icp-audit", method: "GET" }),
        }),
        // Moves the organization off the audit plan onto its trial.
        startIcpAuditTrial: builder.mutation<void, void>({
            query: () => ({ url: "icp-audit/trial", method: "POST" }),
            invalidatesTags: [{ type: "User", id: "LIST" }],
        }),
        getIcpVocabulary: builder.query<
            paths["/icp-profile/vocabulary"]["get"]["responses"]["200"]["content"]["application/json"],
            undefined
        >({
            query: () => ({ url: "icp-profile/vocabulary", method: "GET" }),
            keepUnusedDataFor: 3600,
        }),
        saveIcpProfile: builder.mutation<
            paths["/icp-profile"]["put"]["responses"]["200"]["content"]["application/json"],
            IcpProfileSaveBody
        >({
            extraOptions: { errorToast: false },
            query: (body) => ({ url: "icp-profile", method: "PUT", body }),
            invalidatesTags: [
                { type: "IcpProfile", id: "CURRENT" },
                "Workspaces",
            ],
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                await queryFulfilled;
                dispatch(dataApi.util.invalidateTags(["Companies"]));
            },
        }),
        draftIcpProfile: builder.mutation<
            paths["/icp-profile/draft"]["post"]["responses"]["200"]["content"]["application/json"],
            paths["/icp-profile/draft"]["post"]["requestBody"]["content"]["application/json"]
        >({
            extraOptions: { errorToast: false },
            // Reading a website takes longer than one request may run, so
            // the Api drafts on a queue and answers 202 until the draft is
            // ready. Asked again every few seconds, the mutation stays one
            // call to its caller: loading until there is a draft or an error.
            queryFn: async (body, _api, _extraOptions, baseQuery) =>
                postUntilReady<
                    paths["/icp-profile/draft"]["post"]["responses"]["200"]["content"]["application/json"],
                    ApiError
                >(async (arg) => baseQuery(arg), "icp-profile/draft", body, {
                    status: "CUSTOM_ERROR",
                    error: "Draft timed out",
                    message:
                        "Reading this website is taking too long. Please try again in a moment.",
                }),
        }),
        backtestIcpProfile: builder.mutation<
            paths["/icp-profile/backtest"]["post"]["responses"]["200"]["content"]["application/json"],
            IcpProfileBacktestBody
        >({
            extraOptions: { errorToast: false },
            // Asking a sample spends the classification allowance the
            // profile's usage meter shows.
            invalidatesTags: (_result, _error, body) =>
                body.ask === false
                    ? []
                    : [{ type: "IcpProfile", id: "CURRENT" }],
            // A profile's questions are asked of a sample of companies
            // before the test can score them, on the same queue-and-202
            // footing as the draft.
            queryFn: async (body, _api, _extraOptions, baseQuery) =>
                postUntilReady<
                    paths["/icp-profile/backtest"]["post"]["responses"]["200"]["content"]["application/json"],
                    ApiError
                >(async (arg) => baseQuery(arg), "icp-profile/backtest", body, {
                    status: "CUSTOM_ERROR",
                    error: "Test timed out",
                    message:
                        "Asking your questions is taking too long. Please try again in a moment.",
                }),
        }),
    };
}

function getLensPaths(builder: ApiEndpointBuilder) {
    return {
        getLensDetails: builder.query<
            paths["/user/organization/lens/details"]["get"]["responses"]["200"]["content"]["application/json"],
            undefined
        >({
            query: () => ({
                url: "user/organization/lens/details",
                method: "GET",
            }),
            providesTags: [{ type: "LensDetails", id: "LIST" }],
        }),
        claimLensTrialCredits: builder.mutation<void, undefined>({
            query: () => ({
                url: "user/organization/lens/claim-trial-credits",
                method: "POST",
            }),
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    // Update the cached lens details with trial credits claimed
                    dispatch(
                        api.util.updateQueryData(
                            "getLensDetails",
                            undefined,
                            (draft) => {
                                draft.trialCreditsClaimed = true;
                                draft.creditsGranted =
                                    draft.creditsGranted || 0;
                                draft.creditsGranted += 25;
                            },
                        ),
                    );
                } catch {
                    // Error handled by the component
                }
            },
        }),
    };
}

function getUsagePaths(builder: ApiEndpointBuilder) {
    return {
        getOrganizationUsage: builder.query<
            paths["/user/organization/usage"]["get"]["responses"]["200"]["content"]["application/json"],
            undefined
        >({
            query: () => ({
                url: "user/organization/usage",
                method: "GET",
            }),
            // A tagging tier bought or changed moves the allowance the
            // meter is drawn against.
            providesTags: [{ type: "OrganizationUsage", id: "LIST" }],
        }),
    };
}

function getInvoicePaths(builder: ApiEndpointBuilder) {
    return {
        getOrganizationInvoices: builder.query<
            OrganizationInvoiceList,
            undefined
        >({
            query: () => ({
                url: "user/organization/invoices",
                method: "GET",
            }),
        }),
    };
}

function getWorkspaceIntegrationPaths(builder: ApiEndpointBuilder) {
    return {
        getWorkspaceIntegrations: builder.query<
            paths["/workspace-integrations"]["get"]["responses"]["200"]["content"]["application/json"],
            undefined
        >({
            query: () => ({
                url: `workspace-integrations`,
                method: "GET",
            }),
            providesTags: (result) =>
                result
                    ? [
                          ...result.map(
                              ({ id }) =>
                                  ({
                                      type: "WorkspaceIntegrations",
                                      id,
                                  }) as const,
                          ),
                          { type: "WorkspaceIntegrations", id: "LIST" },
                      ]
                    : [{ type: "WorkspaceIntegrations", id: "LIST" }],
        }),
        getWorkspaceIntegration: builder.query<
            paths["/workspace-integrations/{id}"]["get"]["responses"]["200"]["content"]["application/json"],
            paths["/workspace-integrations/{id}"]["get"]["parameters"]["path"]
        >({
            query: ({ id }) => ({
                url: `workspace-integrations/${id}`,
                method: "GET",
            }),
        }),
        createWorkspaceIntegration: builder.mutation<
            paths["/workspace-integrations"]["post"]["responses"]["201"]["content"]["application/json"] & {
                connect_session_token: string;
            },
            paths["/workspace-integrations"]["post"]["requestBody"]["content"]["application/json"]
        >({
            query(data) {
                return {
                    url: `workspace-integrations`,
                    method: "POST",
                    body: data,
                };
            },
        }),
        deleteWorkspaceIntegration: builder.mutation<
            paths["/workspace-integrations/{id}"]["delete"]["responses"]["204"],
            paths["/workspace-integrations/{id}"]["delete"]["parameters"]["path"]
        >({
            query: ({ id }) => ({
                url: `workspace-integrations/${id}`,
                method: "DELETE",
            }),
            invalidatesTags: [
                { type: "WorkspaceIntegrations", id: "LIST" },
                { type: "Automations", id: "LIST" },
            ],
            async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    api.util.updateQueryData(
                        "getWorkspaceIntegrations",
                        undefined,
                        (draft) => {
                            const index = draft.findIndex((wi) => wi.id === id);
                            draft.splice(index, 1);
                        },
                    ),
                );

                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
        }),
        getWorkspaceIntegrationSelectableAccounts: builder.mutation<
            paths["/workspace-integrations/{id}/selectable-accounts"]["get"]["responses"]["200"]["content"]["application/json"],
            paths["/workspace-integrations/{id}/selectable-accounts"]["get"]["parameters"]["path"]
        >({
            query: ({ id }) => ({
                url: `workspace-integrations/${id}/selectable-accounts`,
                method: "GET",
            }),
        }),
        updateWorkspaceIntegration: builder.mutation<
            paths["/workspace-integrations/{id}"]["patch"]["responses"]["200"]["content"]["application/json"],
            paths["/workspace-integrations/{id}"]["patch"]["requestBody"]["content"]["application/json"] &
                paths["/workspace-integrations/{id}"]["patch"]["parameters"]["path"]
        >({
            query: ({ id, ...data }) => ({
                url: `workspace-integrations/${id}`,
                method: "PATCH",
                body: data,
            }),
            invalidatesTags: [{ type: "WorkspaceIntegrations", id: "LIST" }],
        }),
        getWorkspaceIntegrationSettings: builder.query<
            paths["/workspace-integrations/{id}/settings"]["get"]["responses"]["200"]["content"]["application/json"],
            paths["/workspace-integrations/{id}/settings"]["get"]["parameters"]["path"]
        >({
            query: ({ id }) => ({
                url: `workspace-integrations/${id}/settings`,
                method: "GET",
            }),
        }),
        updateWorkspaceIntegrationSettings: builder.mutation<
            paths["/workspace-integrations/{id}/settings"]["get"]["responses"]["200"]["content"]["application/json"],
            paths["/workspace-integrations/{id}/settings"]["patch"]["parameters"]["path"]
        >({
            query: ({ id, ...data }) => ({
                url: `workspace-integrations/${id}/settings`,
                method: "PATCH",
                body: data,
            }),
            invalidatesTags: [{ type: "WorkspaceIntegrations", id: "LIST" }],
        }),
        generateHubspotInstallState: builder.mutation<
            { state: string; returnUrl: string },
            { return_url: string }
        >({
            extraOptions: { errorToast: false },
            query: (data) => ({
                url: `oauth/hubspot/install/state`,
                method: "POST",
                body: data,
            }),
        }),
        finalizeHubspotInstall: builder.mutation<
            { returnUrl: string },
            { code: string; state: string }
        >({
            extraOptions: { errorToast: false },
            query: (data) => ({
                url: `oauth/hubspot/install/finalize`,
                method: "POST",
                body: data,
            }),
            invalidatesTags: [{ type: "WorkspaceIntegrations", id: "LIST" }],
        }),
        refreshWorkspaceIntegrationMetadata: builder.mutation<
            paths["/workspace-integrations/{id}/refresh-metadata"]["post"]["responses"]["200"]["content"]["application/json"],
            paths["/workspace-integrations/{id}/refresh-metadata"]["post"]["parameters"]["path"]
        >({
            query: ({ id }) => ({
                url: `workspace-integrations/${id}/refresh-metadata`,
                method: "POST",
            }),
            invalidatesTags: [{ type: "WorkspaceIntegrations", id: "LIST" }],
        }),
        createWorkspaceIntegrationConnectSession: builder.mutation<
            { token: string },
            { id: string; grants?: IntegrationGrant[] }
        >({
            query: ({ id, grants = [] }) => ({
                url: `workspace-integrations/${id}/connect-session`,
                method: "POST",
                // Whatever the connection already holds is asked for again by the Api,
                // so reconnecting never quietly narrows a grant somebody chose.
                body: { grants },
            }),
        }),
        // Dev-only: stands in for the Nango auth.creation webhook, which can't
        // reach localhost. The endpoint exists only when the backend runs in
        // the `dev` environment (provided by App\DevBundle).
        simulateNangoConnection: builder.mutation<
            paths["/dev/workspace-integrations/{id}/simulate-nango-connection"]["post"]["responses"]["200"]["content"]["application/json"],
            { id: string; connectionId: string }
        >({
            query: ({ id, connectionId }) => ({
                url: `dev/workspace-integrations/${id}/simulate-nango-connection`,
                method: "POST",
                body: { connection_id: connectionId },
            }),
        }),
    };
}

type ReplaceFunnelProps<T> = Omit<
    T,
    | "steps"
    | "filters"
    | "reportType"
    | "attributionModel"
    | "drillDownDimensions"
> &
    FunnelConfig;

function getFunnelPaths(builder: ApiEndpointBuilder) {
    return {
        getFunnels: builder.query<FunnelConfig[], undefined>({
            query: () => ({
                url: `funnels`,
                method: "GET",
            }),
            providesTags: (result) =>
                result
                    ? [
                          ...result.map(
                              ({ name }) =>
                                  ({
                                      type: "Funnels",
                                      name,
                                  }) as const,
                          ),
                          { type: "Funnels", id: "LIST" },
                      ]
                    : [{ type: "Funnels", id: "LIST" }],
        }),
        createFunnel: builder.mutation<
            ReplaceFunnelProps<
                paths["/funnels"]["post"]["responses"]["201"]["content"]["application/json"]
            >,
            ReplaceFunnelProps<
                Omit<
                    paths["/funnels"]["post"]["requestBody"]["content"]["application/json"],
                    "id"
                >
            >
        >({
            query: (data) => ({
                url: `funnels`,
                method: "POST",
                body: data,
            }),
            async onQueryStarted(data, { dispatch, queryFulfilled }) {
                try {
                    dispatch(
                        api.util.updateQueryData(
                            "getFunnels",
                            undefined,
                            (draft) => {
                                draft.push(data);
                            },
                        ),
                    );
                    const res = await queryFulfilled;
                    dispatch(
                        api.util.updateQueryData(
                            "getFunnels",
                            undefined,
                            (draft) => {
                                const index = draft.findIndex((x) => !x.id);
                                if (index >= 0) {
                                    draft.splice(index, 1);
                                }
                                draft.push(res.data);
                            },
                        ),
                    );
                } catch {
                    // nothing to do
                }
            },
        }),
        updateFunnel: builder.mutation<
            ReplaceFunnelProps<
                paths["/funnels/{id}"]["patch"]["responses"]["200"]["content"]["application/json"]
            >,
            ReplaceFunnelProps<
                paths["/funnels/{id}"]["patch"]["requestBody"]["content"]["application/json"] &
                    paths["/funnels/{id}"]["patch"]["parameters"]["path"]
            >
        >({
            query: (data) => ({
                url: `funnels/${data.id}`,
                method: "PATCH",
                body: data,
            }),
            async onQueryStarted(data, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    api.util.updateQueryData(
                        "getFunnels",
                        undefined,
                        (draft) => {
                            const funnel = draft.find(
                                (funnel) => funnel.id === data.id,
                            );
                            assertNotUndefined(funnel);
                            Object.assign(funnel, data);
                        },
                    ),
                );

                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
        }),
        deleteFunnel: builder.mutation<
            paths["/funnels/{id}"]["delete"]["responses"]["204"],
            paths["/funnels/{id}"]["delete"]["parameters"]["path"]
        >({
            query({ id }) {
                return {
                    url: `funnels/${id}`,
                    method: "DELETE",
                };
            },
            async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    api.util.updateQueryData(
                        "getFunnels",
                        undefined,
                        (draft) => {
                            const index = draft.findIndex(
                                (funnel) => funnel.id === id,
                            );
                            draft.splice(index, 1);
                        },
                    ),
                );

                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
            invalidatesTags: [{ type: "Funnels", id: "LIST" }],
        }),
    };
}

type ReplaceReportProps<T> = Omit<
    T,
    | "filters"
    | "reportType"
    | "attributionModel"
    | "drillDownDimensions"
    | "sortBy"
> &
    SavedReportConfig;

function getReportPaths(builder: ApiEndpointBuilder) {
    return {
        getReports: builder.query<SavedReportConfig[], undefined>({
            query: () => ({
                url: `reports`,
                method: "GET",
            }),
            providesTags: (result) =>
                result
                    ? [
                          ...result.map(
                              ({ name }) =>
                                  ({
                                      type: "Reports",
                                      name,
                                  }) as const,
                          ),
                          { type: "Reports", id: "LIST" },
                      ]
                    : [{ type: "Reports", id: "LIST" }],
        }),
        createReport: builder.mutation<
            ReplaceReportProps<
                paths["/reports"]["post"]["responses"]["201"]["content"]["application/json"]
            >,
            ReplaceReportProps<
                Omit<
                    paths["/reports"]["post"]["requestBody"]["content"]["application/json"],
                    "id"
                >
            >
        >({
            query: (data) => ({
                url: `reports`,
                method: "POST",
                body: data,
            }),
            async onQueryStarted(data, { dispatch, queryFulfilled }) {
                try {
                    dispatch(
                        api.util.updateQueryData(
                            "getReports",
                            undefined,
                            (draft) => {
                                draft.push(data as SavedReportConfig);
                            },
                        ),
                    );
                    const res = await queryFulfilled;
                    dispatch(
                        api.util.updateQueryData(
                            "getReports",
                            undefined,
                            (draft) => {
                                const index = draft.findIndex((x) => !x.id);
                                if (index >= 0) {
                                    draft.splice(index, 1);
                                }
                                draft.push(res.data);
                            },
                        ),
                    );
                } catch {
                    // nothing to do
                }
            },
        }),
        updateReport: builder.mutation<
            ReplaceReportProps<
                paths["/reports/{id}"]["patch"]["responses"]["200"]["content"]["application/json"]
            >,
            ReplaceReportProps<
                paths["/reports/{id}"]["patch"]["requestBody"]["content"]["application/json"] &
                    paths["/reports/{id}"]["patch"]["parameters"]["path"]
            >
        >({
            query: (data) => ({
                url: `reports/${data.id}`,
                method: "PATCH",
                body: data,
            }),
            async onQueryStarted(data, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    api.util.updateQueryData(
                        "getReports",
                        undefined,
                        (draft) => {
                            const report = draft.find(
                                (report) => report.id === data.id,
                            );
                            assertNotUndefined(report);
                            Object.assign(report, data);
                        },
                    ),
                );

                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
        }),
        deleteReport: builder.mutation<
            paths["/reports/{id}"]["delete"]["responses"]["204"],
            paths["/reports/{id}"]["delete"]["parameters"]["path"]
        >({
            query({ id }) {
                return {
                    url: `reports/${id}`,
                    method: "DELETE",
                };
            },
            async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    api.util.updateQueryData(
                        "getReports",
                        undefined,
                        (draft) => {
                            const index = draft.findIndex(
                                (report) => report.id === id,
                            );
                            draft.splice(index, 1);
                        },
                    ),
                );

                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
            invalidatesTags: [{ type: "Reports", id: "LIST" }],
        }),
    };
}

export type ReplaceAudienceProps<T> = Omit<T, "filters" | "sortBy"> &
    AudienceConfig;

function getAudiencePaths(builder: ApiEndpointBuilder) {
    return {
        getAudiences: builder.query<
            ReplaceAudienceProps<
                paths["/audiences"]["get"]["responses"]["200"]["content"]["application/json"]
            >,
            undefined
        >({
            query: () => ({
                url: `audiences`,
                method: "GET",
            }),
            providesTags: (result) =>
                result
                    ? [
                          ...result.map(
                              ({ name }) =>
                                  ({
                                      type: "Audiences",
                                      name,
                                  }) as const,
                          ),
                          { type: "Audiences", id: "LIST" },
                      ]
                    : [{ type: "Audiences", id: "LIST" }],
        }),
        createAudience: builder.mutation<
            ReplaceAudienceProps<
                paths["/audiences"]["post"]["responses"]["201"]["content"]["application/json"]
            >,
            ReplaceAudienceProps<
                Omit<
                    paths["/audiences"]["post"]["requestBody"]["content"]["application/json"],
                    "id"
                >
            >
        >({
            query(data) {
                return {
                    url: `audiences`,
                    method: "POST",
                    body: data,
                };
            },
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                const res = await queryFulfilled;
                dispatch(
                    api.util.updateQueryData(
                        "getAudiences",
                        undefined,
                        (draft) => {
                            // @ts-expect-error Not sure why there's an error here
                            draft.push(res.data);
                        },
                    ),
                );
            },
        }),
        updateAudience: builder.mutation<
            ReplaceAudienceProps<
                paths["/audiences/{id}"]["patch"]["responses"]["200"]["content"]["application/json"]
            >,
            ReplaceAudienceProps<
                paths["/audiences/{id}"]["patch"]["requestBody"]["content"]["application/json"] &
                    paths["/audiences/{id}"]["patch"]["parameters"]["path"]
            >
        >({
            query: (data) => ({
                url: `audiences/${data.id}`,
                method: "PATCH",
                body: data,
            }),
            async onQueryStarted(data, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    api.util.updateQueryData(
                        "getAudiences",
                        undefined,
                        (draft) => {
                            const contactSegment = draft.find(
                                (contactSegment) =>
                                    contactSegment.id === data.id,
                            );
                            assertNotUndefined(contactSegment);
                            Object.assign(contactSegment, data);
                        },
                    ),
                );

                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
        }),
        deleteAudience: builder.mutation<
            paths["/audiences/{id}"]["delete"]["responses"]["204"],
            paths["/audiences/{id}"]["delete"]["parameters"]["path"]
        >({
            query({ id }) {
                return {
                    url: `audiences/${id}`,
                    method: "DELETE",
                };
            },
            async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    api.util.updateQueryData(
                        "getAudiences",
                        undefined,
                        (draft) => {
                            const index = draft.findIndex(
                                (contactSegment) => contactSegment.id === id,
                            );
                            draft.splice(index, 1);
                        },
                    ),
                );

                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
            invalidatesTags: [{ type: "Audiences", id: "LIST" }],
        }),
        identifyContact: builder.mutation<
            paths["/contacts"]["post"]["responses"]["204"],
            Partial<
                paths["/contacts"]["post"]["requestBody"]["content"]["application/json"]
            > & {
                workspace: Workspace;
            }
        >({
            query: (data) => {
                return {
                    url: `contacts`,
                    method: "POST",
                    body: data,
                };
            },
            async onQueryStarted(
                { traits, userId, workspace, email, leadScore },
                { dispatch, queryFulfilled },
            ) {
                const patchResult = dispatch(
                    dataApi.util.updateQueryData(
                        "getContact",
                        {
                            workspaceId: workspace.id,
                            cutOffEvents: workspace.cutOffEvents,
                            userIdOrEmail: (userId || email)!,
                            // Every field here is part of the cache key, so the
                            // timezone has to be resolved exactly like
                            // `useReportingTimezone` does for the real query --
                            // otherwise this patch lands on a key nothing reads
                            // and the optimistic update silently no-ops.
                            timezone: workspace.timezone ?? null,
                            defaultCurrency: workspace.defaultCurrency,
                            reportingCurrency:
                                workspace.reportingCurrency || null,
                        },
                        (draft) => {
                            draft.data.traits = {
                                ...draft.data.traits,
                                ...traits,
                            };

                            draft.data.leadScore =
                                typeof leadScore !== "undefined"
                                    ? leadScore
                                    : null;
                        },
                    ),
                );

                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
        }),
        groupCompany: builder.mutation<
            paths["/companies"]["post"]["responses"]["204"],
            Partial<
                Omit<
                    paths["/companies"]["post"]["requestBody"]["content"]["application/json"],
                    "userId" | "trackingSource"
                >
            > & {
                workspace: Workspace;
            }
        >({
            query: (data) => {
                return {
                    url: `companies`,
                    method: "POST",
                    body: {
                        group_id: data.groupId,
                        website: data.website,
                        name: data.name,
                        lead_score: data.leadScore,
                        traits: data.traits,
                    },
                };
            },
            async onQueryStarted(
                { traits, groupId, website, workspace, leadScore },
                { dispatch, queryFulfilled },
            ) {
                const companyId = groupId || website;
                if (!companyId) {
                    return;
                }

                const patchResult = dispatch(
                    dataApi.util.updateQueryData(
                        "getCompany",
                        {
                            workspaceId: workspace.id,
                            cutOffEvents: workspace.cutOffEvents,
                            companyId: companyId,
                            timezone: workspace.timezone ?? null,
                            defaultCurrency: workspace.defaultCurrency,
                            reportingCurrency:
                                workspace.reportingCurrency || null,
                            icpProfile: icpProfileOf(workspace),
                        },
                        (draft) => {
                            draft.data.traits = {
                                ...draft.data.traits,
                                ...traits,
                            };

                            draft.data.leadScore =
                                typeof leadScore !== "undefined"
                                    ? leadScore
                                    : null;
                        },
                    ),
                );

                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
        }),
        importData: builder.mutation<void, ImportDataRequest>({
            query: (data) => ({
                url: `data/import`,
                method: "POST",
                body: data,
            }),
        }),
    };
}

function getClientManagerPaths(builder: ApiEndpointBuilder) {
    return {
        getTrackingPlanWorkspaceSummaries: builder.query<
            TrackingPlanWorkspaceSummary[],
            undefined
        >({
            query: () => ({
                url: "client-manager/tracking-plan-summary",
                method: "GET",
            }),
            providesTags: ["Workspaces"],
        }),
        getClientManagerWorkspaceHealth: builder.query<
            ClientManagerWorkspaceHealth[],
            undefined
        >({
            query: () => ({
                url: "client-manager/workspace-health",
                method: "GET",
            }),
            providesTags: ["Workspaces"],
        }),
        clientManagerCreateClient: builder.mutation<
            paths["/client-manager"]["post"]["responses"]["201"]["content"]["application/json"],
            paths["/client-manager"]["post"]["requestBody"]["content"]["application/json"]
        >({
            query: (data) => ({
                url: `client-manager`,
                method: "POST",
                body: data,
            }),
            invalidatesTags: ["Workspaces"],
        }),
        clientManagerUpdateOrganization: builder.mutation<
            paths["/client-manager/{id}/organization"]["patch"]["responses"]["200"]["content"]["application/json"],
            Partial<
                paths["/client-manager/{id}/organization"]["patch"]["requestBody"]["content"]["application/json"]
            > &
                paths["/client-manager/{id}/organization"]["patch"]["parameters"]["path"]
        >({
            query: ({ id, ...data }) => {
                return {
                    url: `client-manager/${id}/organization`,
                    method: "PATCH",
                    body: data,
                };
            },
            async onQueryStarted(
                { id, ...orgData },
                { dispatch, queryFulfilled },
            ) {
                const userPatchResult = dispatch(
                    api.util.updateQueryData("getUser", undefined, (draft) => {
                        merge(draft.currentOrganization, orgData);
                    }),
                );

                const workspacePatchResult = dispatch(
                    api.util.updateQueryData(
                        "getWorkspaces",
                        undefined,
                        (draft) => {
                            draft.forEach((ws) => {
                                if (!!ws && ws.organization.id === id) {
                                    merge(ws.organization, orgData);
                                }
                            });
                        },
                    ),
                );

                try {
                    await queryFulfilled;
                } catch {
                    userPatchResult.undo();
                    workspacePatchResult.undo();
                }
            },
        }),
        clientManagerCreateBillingPortalSession: builder.mutation<
            paths["/client-manager/{id}/subscription-portal-session"]["post"]["responses"]["201"]["content"]["application/json"],
            paths["/client-manager/{id}/subscription-portal-session"]["post"]["parameters"]["path"]
        >({
            query: ({ id }) => ({
                url: `client-manager/${id}/subscription-portal-session`,
                method: "POST",
            }),
        }),
        clientManagerInviteTeamMember: builder.mutation<
            paths["/client-manager/{id}/team-member"]["post"]["responses"]["201"]["content"]["application/json"],
            paths["/client-manager/{id}/team-member"]["post"]["requestBody"]["content"]["application/json"] &
                paths["/client-manager/{id}/team-member"]["post"]["parameters"]["path"]
        >({
            query: ({ id, ...data }) => ({
                url: `client-manager/${id}/team-member`,
                method: "POST",
                body: data,
            }),
            invalidatesTags: ["TeamMembers", "Workspaces"],
        }),
    };
}

function getAudienceSyncPaths(builder: ApiEndpointBuilder) {
    return {
        getAudienceSyncs: builder.query<
            paths["/audience-syncs"]["get"]["responses"]["200"]["content"]["application/json"],
            undefined
        >({
            query: () => ({
                url: `audience-syncs`,
                method: "GET",
            }),
            providesTags: (result) =>
                result
                    ? [
                          ...result.map(
                              ({ id }) =>
                                  ({
                                      type: "AudienceSyncs",
                                      id,
                                  }) as const,
                          ),
                          { type: "AudienceSyncs", id: "LIST" },
                      ]
                    : [{ type: "AudienceSyncs", id: "LIST" }],
        }),
        createAudienceSync: builder.mutation<
            paths["/audience-syncs"]["post"]["responses"]["201"]["content"]["application/json"],
            paths["/audience-syncs"]["post"]["requestBody"]["content"]["application/json"]
        >({
            query: (data) => ({
                url: `audience-syncs`,
                method: "POST",
                body: data,
            }),
            invalidatesTags: [
                { type: "AudienceSyncs", id: "LIST" },
                { type: "Audiences", id: "LIST" },
            ],
        }),
        updateAudienceSync: builder.mutation<
            paths["/audience-syncs/{id}"]["patch"]["responses"]["200"]["content"]["application/json"],
            // A patch: what is left out is left as it is
            Partial<
                paths["/audience-syncs/{id}"]["patch"]["requestBody"]["content"]["application/json"]
            > &
                paths["/audience-syncs/{id}"]["patch"]["parameters"]["path"]
        >({
            query: ({ id, ...data }) => ({
                url: `audience-syncs/${id}`,
                method: "PATCH",
                body: data,
            }),
            invalidatesTags: (_result, _error, { id }) => [
                { type: "AudienceSyncs", id },
            ],
        }),
        getWorkspaceIntegrationCampaigns: builder.query<
            paths["/workspace-integrations/{id}/campaigns"]["get"]["responses"]["200"]["content"]["application/json"],
            paths["/workspace-integrations/{id}/campaigns"]["get"]["parameters"]["path"]
        >({
            query: ({ id }) => ({
                url: `workspace-integrations/${id}/campaigns`,
                method: "GET",
            }),
        }),
        deleteAudienceSync: builder.mutation<
            paths["/audience-syncs/{id}"]["delete"]["responses"]["204"],
            paths["/audience-syncs/{id}"]["delete"]["parameters"]["path"]
        >({
            query: ({ id }) => ({
                url: `audience-syncs/${id}`,
                method: "DELETE",
            }),
            invalidatesTags: [
                { type: "AudienceSyncs", id: "LIST" },
                { type: "Audiences", id: "LIST" },
            ],
            async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    api.util.updateQueryData(
                        "getAudienceSyncs",
                        undefined,
                        (draft) => {
                            const index = draft.findIndex(
                                (sync) => sync.id === id,
                            );
                            draft.splice(index, 1);
                        },
                    ),
                );

                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
        }),
        getAudienceSyncAvailableSettings: builder.query<
            paths["/audience-syncs-settings/{type}/{id}"]["get"]["responses"]["200"]["content"]["application/json"],
            paths["/audience-syncs-settings/{type}/{id}"]["get"]["parameters"]["path"] &
                paths["/audience-syncs-settings/{type}/{id}"]["get"]["parameters"]["query"]
        >({
            query: ({ type, id, audience_type }) => ({
                url: `audience-syncs-settings/${type}/${id}`,
                method: "GET",
                params: {
                    ...(audience_type ? { audience_type } : {}),
                },
            }),
        }),
    };
}

function getConversionSyncPaths(builder: ApiEndpointBuilder) {
    return {
        getConversionSyncs: builder.query<
            paths["/conversion-syncs"]["get"]["responses"]["200"]["content"]["application/json"],
            undefined
        >({
            query: () => ({
                url: `conversion-syncs`,
                method: "GET",
            }),
            providesTags: (result) =>
                result
                    ? [
                          ...result.map(
                              ({ id }) =>
                                  ({
                                      type: "ConversionSyncs",
                                      id,
                                  }) as const,
                          ),
                          { type: "ConversionSyncs", id: "LIST" },
                      ]
                    : [{ type: "ConversionSyncs", id: "LIST" }],
        }),
        createConversionSync: builder.mutation<
            paths["/conversion-syncs"]["post"]["responses"]["201"]["content"]["application/json"],
            paths["/conversion-syncs"]["post"]["requestBody"]["content"]["application/json"]
        >({
            query: (data) => ({
                url: `conversion-syncs`,
                method: "POST",
                body: data,
            }),
            invalidatesTags: [{ type: "ConversionSyncs", id: "LIST" }],
        }),
        updateConversionSync: builder.mutation<
            paths["/conversion-syncs/{id}"]["patch"]["responses"]["200"]["content"]["application/json"],
            paths["/conversion-syncs/{id}"]["patch"]["requestBody"]["content"]["application/json"] &
                paths["/conversion-syncs/{id}"]["patch"]["parameters"]["path"]
        >({
            query: ({ id, ...data }) => ({
                url: `conversion-syncs/${id}`,
                method: "PATCH",
                body: data,
            }),
            invalidatesTags: [{ type: "ConversionSyncs", id: "LIST" }],
        }),
        deleteConversionSync: builder.mutation<undefined, { id: string }>({
            query: ({ id }) => ({
                url: `conversion-syncs/${id}`,
                method: "DELETE",
            }),
            invalidatesTags: [{ type: "ConversionSyncs", id: "LIST" }],
            async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    api.util.updateQueryData(
                        "getConversionSyncs",
                        undefined,
                        (draft) => {
                            const index = draft.findIndex(
                                (sync) => sync.id === id,
                            );
                            draft.splice(index, 1);
                        },
                    ),
                );

                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
        }),
        getConversionSyncAvailableSettings: builder.query<
            paths["/audience-syncs-settings/{type}/{id}"]["get"]["responses"]["200"]["content"]["application/json"],
            { id: string }
        >({
            query: ({ id }) => ({
                url: `conversion-syncs-settings/${id}`,
                method: "GET",
            }),
        }),
    };
}

function getAutomationPaths(builder: ApiEndpointBuilder) {
    return {
        getAutomations: builder.query<
            paths["/automations"]["get"]["responses"]["200"]["content"]["application/json"],
            undefined
        >({
            query: () => ({
                url: `automations`,
                method: "GET",
            }),
            providesTags: (result) =>
                result
                    ? [
                          ...result.map(
                              ({ name }) =>
                                  ({
                                      type: "Automations",
                                      name,
                                  }) as const,
                          ),
                          { type: "Automations", id: "LIST" },
                      ]
                    : [{ type: "Automations", id: "LIST" }],
        }),
        sendAutomationTest: builder.mutation<
            paths["/automations/send-test"]["post"]["responses"]["204"],
            Omit<
                paths["/automations/send-test"]["post"]["requestBody"]["content"]["application/json"],
                "id"
            >
        >({
            query: (data) => ({
                url: `automations/send-test`,
                method: "POST",
                body: data,
            }),
            invalidatesTags: [{ type: "Workspaces", id: "LIST" }],
        }),
        getAutomationAttempts: builder.query<
            paths["/automations/attempts"]["get"]["responses"]["200"]["content"]["application/json"],
            {
                lastEvaluatedKey?: string;
            }
        >({
            query: ({ lastEvaluatedKey }) => {
                return {
                    url: `automations/attempts`,
                    method: "GET",
                    ...(lastEvaluatedKey
                        ? {
                              params: {
                                  last_evaluated_key: lastEvaluatedKey,
                              },
                          }
                        : {}),
                };
            },
        }),
        createAutomation: builder.mutation<
            paths["/automations"]["post"]["responses"]["201"]["content"]["application/json"],
            Omit<
                paths["/automations"]["post"]["requestBody"]["content"]["application/json"],
                "id"
            >
        >({
            query: (data) => ({
                url: `automations`,
                method: "POST",
                body: data,
            }),
            async onQueryStarted(_data, { dispatch, queryFulfilled }) {
                try {
                    const res = await queryFulfilled;
                    dispatch(
                        api.util.updateQueryData(
                            "getAutomations",
                            undefined,
                            (draft) => {
                                draft.push(res.data);
                            },
                        ),
                    );
                } catch {
                    // nothing to do
                }
            },
        }),
        updateAutomation: builder.mutation<
            paths["/automations/{id}"]["patch"]["responses"]["200"]["content"]["application/json"],
            paths["/automations/{id}"]["patch"]["requestBody"]["content"]["application/json"] &
                paths["/automations/{id}"]["patch"]["parameters"]["path"]
        >({
            query: (data) => ({
                url: `automations/${data.id}`,
                method: "PATCH",
                body: data,
            }),
            async onQueryStarted(data, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    api.util.updateQueryData(
                        "getAutomations",
                        undefined,
                        (draft) => {
                            const automation = draft.find(
                                (automation) => automation.id === data.id,
                            );
                            assertNotUndefined(automation);
                            Object.assign(automation, data);
                        },
                    ),
                );

                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
        }),
        deleteAutomation: builder.mutation<
            paths["/automations/{id}"]["delete"]["responses"]["204"],
            paths["/automations/{id}"]["delete"]["parameters"]["path"]
        >({
            query({ id }) {
                return {
                    url: `automations/${id}`,
                    method: "DELETE",
                };
            },
            async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    api.util.updateQueryData(
                        "getAutomations",
                        undefined,
                        (draft) => {
                            const index = draft.findIndex(
                                (automation) => automation.id === id,
                            );
                            draft.splice(index, 1);
                        },
                    ),
                );

                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
            invalidatesTags: [{ type: "Automations", id: "LIST" }],
        }),
    };
}

// Every cached `getChecks` list, whatever statuses it was asked for. An
// optimistic patch has to reach all of them: the action list asks for one
// combination, and anything else that mounts asks for its own.
function cachedCheckListArgs(
    state: Parameters<typeof api.util.selectCachedArgsForQuery>[0],
) {
    return api.util.selectCachedArgsForQuery(state, "getChecks");
}

// The action-list checks Api.
function getCheckPaths(builder: ApiEndpointBuilder) {
    return {
        getChecks: builder.query<
            CheckResult[],
            {
                statuses?: CheckResultStatus[];
                includeSuppressed?: boolean;
            }
        >({
            query: ({ statuses, includeSuppressed }) => ({
                url: `checks`,
                method: "GET",
                params: {
                    ...(statuses?.length ? { status: statuses.join(",") } : {}),
                    ...(includeSuppressed ? { include_suppressed: true } : {}),
                },
            }),
            providesTags: [{ type: "Checks", id: "LIST" }],
        }),
        getCheckCatalog: builder.query<CheckCatalogEntry[], undefined>({
            query: () => ({
                url: `checks/catalog`,
                method: "GET",
            }),
            providesTags: [{ type: "Checks", id: "CATALOG" }],
        }),
        getCheckSummary: builder.query<CheckSummaryResponse, undefined>({
            query: () => ({
                url: `checks/summary`,
                method: "GET",
            }),
            providesTags: [{ type: "Checks", id: "SUMMARY" }],
        }),
        getWithheldChecks: builder.query<CheckWithheldResponse, undefined>({
            query: () => ({
                url: `checks/withheld`,
                method: "GET",
            }),
            providesTags: [{ type: "Checks", id: "WITHHELD" }],
        }),
        getMaturity: builder.query<
            MaturityResponse,
            {
                dateFrom: string;
                dateTo: string;
                compareDateFrom: string;
                compareDateTo: string;
            }
        >({
            query: ({ dateFrom, dateTo, compareDateFrom, compareDateTo }) => ({
                url: `maturity`,
                method: "GET",
                params: {
                    date_from: dateFrom,
                    date_to: dateTo,
                    compare_date_from: compareDateFrom,
                    compare_date_to: compareDateTo,
                },
            }),
        }),
        updateCheck: builder.mutation<
            CheckResult,
            {
                id: string;
                action: CheckAction;
                snoozedUntil?: string;
            }
        >({
            query: ({ id, action, snoozedUntil }) => ({
                url: `checks/${id}`,
                method: "PATCH",
                body: {
                    action,
                    ...(snoozedUntil ? { snoozedUntil } : {}),
                },
            }),
            invalidatesTags: [
                { type: "Checks", id: "LIST" },
                { type: "Checks", id: "SUMMARY" },
            ],
            // Snoozing and dismissing are how the action list is worked down,
            // so the item has to leave the queue on the click rather than on
            // the round trip: the next one is already being read by then.
            async onQueryStarted(
                { id, action, snoozedUntil },
                { dispatch, getState, queryFulfilled },
            ) {
                const patches = cachedCheckListArgs(getState()).map((args) =>
                    dispatch(
                        api.util.updateQueryData("getChecks", args, (draft) => {
                            const item = draft.find((item) => item.id === id);
                            if (!item) {
                                return;
                            }

                            if (action === "snooze") {
                                item.status = "snoozed";
                                item.snoozedUntil = snoozedUntil ?? null;
                            }
                            if (action === "dismiss") {
                                item.status = "dismissed";
                            }
                            if (action === "reopen") {
                                item.status = "open";
                                item.snoozedUntil = null;
                                item.dismissedAt = null;
                            }
                        }),
                    ),
                );

                try {
                    await queryFulfilled;
                } catch {
                    patches.forEach((patch) => patch.undo());
                }
            },
        }),
        muteCheck: builder.mutation<undefined, { checkId: string }>({
            query: ({ checkId }) => ({
                url: `check-mutes/${checkId}`,
                method: "PUT",
            }),
            invalidatesTags: [
                { type: "Checks", id: "LIST" },
                { type: "Checks", id: "SUMMARY" },
                { type: "Checks", id: "CATALOG" },
            ],
            // The catalog is what the action list reads muting off, so
            // flipping the flag here is enough to drop the check's items from
            // the queue immediately.
            async onQueryStarted({ checkId }, { dispatch, queryFulfilled }) {
                const patch = dispatch(
                    api.util.updateQueryData(
                        "getCheckCatalog",
                        undefined,
                        (draft) => {
                            const entry = draft.find(
                                (entry) => entry.checkId === checkId,
                            );
                            if (entry) {
                                entry.muted = true;
                            }
                        },
                    ),
                );

                try {
                    await queryFulfilled;
                } catch {
                    patch.undo();
                }
            },
        }),
        unmuteCheck: builder.mutation<undefined, { checkId: string }>({
            query: ({ checkId }) => ({
                url: `check-mutes/${checkId}`,
                method: "DELETE",
            }),
            invalidatesTags: [
                { type: "Checks", id: "LIST" },
                { type: "Checks", id: "SUMMARY" },
                { type: "Checks", id: "CATALOG" },
            ],
            async onQueryStarted({ checkId }, { dispatch, queryFulfilled }) {
                const patch = dispatch(
                    api.util.updateQueryData(
                        "getCheckCatalog",
                        undefined,
                        (draft) => {
                            const entry = draft.find(
                                (entry) => entry.checkId === checkId,
                            );
                            if (entry) {
                                entry.muted = false;
                            }
                        },
                    ),
                );

                try {
                    await queryFulfilled;
                } catch {
                    patch.undo();
                }
            },
        }),
        updateCheckSettings: builder.mutation<
            undefined,
            {
                checkId: string;
                // Full replacement: send every setting of the check, not a
                // delta. Keys are camelCase; the base query snake-cases them
                // on the wire and the backend camelizes them back.
                values: Record<string, boolean | number | string>;
            }
        >({
            query: ({ checkId, values }) => ({
                url: `check-settings/${checkId}`,
                method: "PUT",
                body: values,
            }),
            async onQueryStarted(
                { checkId, values },
                { dispatch, queryFulfilled },
            ) {
                const patchResult = dispatch(
                    api.util.updateQueryData(
                        "getCheckCatalog",
                        undefined,
                        (draft) => {
                            const entry = draft.find(
                                (entry) => entry.checkId === checkId,
                            );
                            assertNotUndefined(entry);
                            for (const setting of entry.settings ?? []) {
                                if (setting.key in values) {
                                    setting.value = values[setting.key]!;
                                }
                            }
                        },
                    ),
                );

                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
            invalidatesTags: [{ type: "Checks", id: "CATALOG" }],
        }),
    };
}

/**
 * The cold path of chat. The hot path — streaming a turn — never comes through
 * here: it is a separate service with its own transport (features/Chat/
 * transport.ts), because php-fpm cannot stream.
 */
function getChatConversationPaths(builder: ApiEndpointBuilder) {
    return {
        /**
         * The chat allowance, from the chat service rather than
         * management-api: only chat resolves the tier and reads the spend,
         * and this is the same function that refuses a turn.
         *
         * A failure is returned, never toasted. The service shares the turns'
         * Lambda and concurrency, so when it cannot answer the page shows no
         * meter and leaves the composer open; a turn's own refusal decides.
         */
        getChatAllowance: builder.query<ChatAllowanceRead, string>({
            queryFn: async (workspaceId) => {
                const requestedAt = Date.now();
                try {
                    const allowance = await fetchChatAllowance(workspaceId);
                    return { data: { allowance, requestedAt } };
                } catch (error) {
                    return { error: apiErrorFromException(error) };
                }
            },
            providesTags: [{ type: "ChatAllowance", id: "CURRENT" }],
            keepUnusedDataFor: 60,
        }),
        getChatConversations: builder.query<ChatConversation[], void>({
            query: () => ({
                url: `chat/conversations`,
                method: "GET",
            }),
            providesTags: [{ type: "ChatConversations", id: "LIST" }],
        }),
        getChatConversation: builder.query<ChatConversationDetail, string>({
            query: (id) => ({
                url: `chat/conversations/${id}`,
                method: "GET",
            }),
            providesTags: (_result, _error, id) => [
                { type: "ChatConversations", id },
            ],
        }),
        deleteChatConversation: builder.mutation<undefined, string>({
            query: (id) => ({
                url: `chat/conversations/${id}`,
                method: "DELETE",
            }),
            invalidatesTags: (_result, _error, id) => [
                { type: "ChatConversations", id: "LIST" },
                { type: "ChatConversations", id },
            ],
        }),

        /**
         * Rate one answer. A PUT because a rating is one row per answer that
         * the user may change as often as they like — pressing the other thumb
         * replaces it rather than adding a second opinion.
         *
         * Deliberately invalidates NOTHING. The obvious tag here is the
         * conversation, but that conversation is on screen: refetching it would
         * re-seed the panel underneath an answer that may still be streaming,
         * to change a thumb the clicking component already knows the state of.
         */
        saveChatMessageFeedback: builder.mutation<
            undefined,
            {
                conversationId: string;
                messageId: string;
                rating: FeedbackRating;
                comment?: string;
            }
        >({
            extraOptions: { errorToast: false },
            query: ({ conversationId, messageId, rating, comment }) => ({
                url: `chat/conversations/${conversationId}/messages/${messageId}/feedback`,
                method: "PUT",
                body: { rating, comment: comment ?? "" },
            }),
        }),

        /** Un-pressing the thumb. */
        deleteChatMessageFeedback: builder.mutation<
            undefined,
            { conversationId: string; messageId: string }
        >({
            extraOptions: { errorToast: false },
            query: ({ conversationId, messageId }) => ({
                url: `chat/conversations/${conversationId}/messages/${messageId}/feedback`,
                method: "DELETE",
            }),
        }),
    };
}

export const {
    useGetIcpAuditQuery,
    useStartIcpAuditTrialMutation,
    useGetIcpProfileQuery,
    useGetIcpVocabularyQuery,
    useSaveIcpProfileMutation,
    useDraftIcpProfileMutation,
    useBacktestIcpProfileMutation,
    useStartSessionMutation,
    useGetUserQuery,
    useUpdateUserMutation,
    useUpdateDashboardLayoutsMutation,
    useDeleteUserMutation,
    useDismissDialogMutation,
    useUpdateOrganizationMutation,
    useAcceptTermsMutation,
    useClientManagerUpdateOrganizationMutation,
    useClientManagerCreateClientMutation,
    useClientManagerCreateBillingPortalSessionMutation,
    useClientManagerInviteTeamMemberMutation,
    useIdentifyContactMutation,
    useGroupCompanyMutation,
    useImportDataMutation,
    useGetWorkspacesQuery,
    useGetTrackingPlanWorkspaceSummariesQuery,
    useGetClientManagerWorkspaceHealthQuery,
    useCreateWorkspaceMutation,
    useUpdateWorkspaceMutation,
    useUpdateLensContactsMutation,
    useDeleteWorkspaceMutation,
    useRotateWebhookSigningSecretMutation,
    useGetAllTeamMembersQuery,
    useGetInvitationsQuery,
    useInviteTeamMemberMutation,
    useUpdateTeamMemberMutation,
    useResendInvitationMutation,
    useRevokeInvitationMutation,
    useDeleteTeamMemberMutation,
    useGetSelfServePlansQuery,
    useStartSubscriptionCheckoutSessionMutation,
    useGetUpgradeSubscriptionPortalSessionMutation,
    useGetManageSubscriptionPortalSessionMutation,
    useGetCheckoutSetupSessionMutation,
    useChangeLensPlanMutation,
    useCancelLensPlanMutation,
    useGetTaggingBillingTermsQuery,
    useChangeTaggingPlanMutation,
    useCancelTaggingPlanMutation,
    useKeepTaggingPlanMutation,
    useGetLensDetailsQuery,
    useGetTaggingContainerQuery,
    useCreateTaggingContainerMutation,
    useUpdateTaggingContainerMutation,
    useSaveTaggingContainerMeasurementIdMutation,
    useSaveTaggingContainerBlockerRecoveryMutation,
    useDeleteTaggingContainerMutation,
    useEmailTaggingContainerDnsRecordsMutation,
    useGetTaggingContainerTagManagerSetupQuery,
    useGetWorkspaceGa4DataStreamsQuery,
    useGetWorkspaceGoogleTagManagerContainersQuery,
    useLazyGetGoogleTagManagerContainerConfigQuery,
    useLazyGetGoogleTagManagerContainerGa4Query,
    useClaimLensTrialCreditsMutation,
    useGetOrganizationUsageQuery,
    useGetOrganizationInvoicesQuery,
    useGetWorkspaceIntegrationsQuery,
    useCreateWorkspaceIntegrationMutation,
    useUpdateWorkspaceIntegrationMutation,
    useDeleteWorkspaceIntegrationMutation,
    useGetWorkspaceIntegrationSelectableAccountsMutation,
    useGetWorkspaceIntegrationSettingsQuery,
    useUpdateWorkspaceIntegrationSettingsMutation,
    useGetFunnelsQuery,
    useUpdateFunnelMutation,
    useDeleteFunnelMutation,
    useCreateFunnelMutation,
    useGetAutomationsQuery,
    useLazyGetAutomationAttemptsQuery,
    useUpdateAutomationMutation,
    useDeleteAutomationMutation,
    useCreateAutomationMutation,
    useSendAutomationTestMutation,
    useGetAudiencesQuery,
    useCreateAudienceMutation,
    useUpdateAudienceMutation,
    useDeleteAudienceMutation,
    useGetAudienceSyncsQuery,
    useCreateAudienceSyncMutation,
    useUpdateAudienceSyncMutation,
    useGetWorkspaceIntegrationCampaignsQuery,
    useGetConversionSyncsQuery,
    useCreateConversionSyncMutation,
    useUpdateConversionSyncMutation,
    useDeleteConversionSyncMutation,
    useGetConversionSyncAvailableSettingsQuery,
    useDeleteAudienceSyncMutation,
    useGetAudienceSyncAvailableSettingsQuery,
    useRefreshWorkspaceIntegrationMetadataMutation,
    useCreateWorkspaceIntegrationConnectSessionMutation,
    useSimulateNangoConnectionMutation,
    useLazyGetWorkspaceIntegrationQuery,
    useGetReportsQuery,
    useCreateReportMutation,
    useUpdateReportMutation,
    useDeleteReportMutation,
    useGetChecksQuery,
    useGetCheckCatalogQuery,
    useGetCheckSummaryQuery,
    useGetWithheldChecksQuery,
    useGetMaturityQuery,
    useUpdateCheckMutation,
    useMuteCheckMutation,
    useUnmuteCheckMutation,
    useUpdateCheckSettingsMutation,
    useGetChatConversationsQuery,
    useGetChatConversationQuery,
    useGetChatAllowanceQuery,
    useDeleteChatConversationMutation,
    useSaveChatMessageFeedbackMutation,
    useDeleteChatMessageFeedbackMutation,
    useGetTrackingPlanQuery,
    useCreateTrackingPlanMutation,
    useSaveTrackingPlanVendorMutation,
    useRemoveTrackingPlanVendorMutation,
    useConfirmTrackingPlanTagMutation,
    useSaveTrackingPlanPolicyRuleMutation,
    useSaveTrackingPlanSiteToolsMutation,
    useRemoveTrackingPlanTagConfirmationMutation,
    useGetTaggingCollectionQuery,
    useCreateTrackingPlanEventMutation,
    useUpdateTrackingPlanEventMutation,
    useDeleteTrackingPlanEventMutation,
    useReorderTrackingPlanEventsMutation,
    useGetTrackingPlanVersionsQuery,
    useGetTrackingPlanVersionQuery,
    useGetTrackingPlanVersionDiffQuery,
    usePromoteTrackingPlanMutation,
    useDiscardTrackingPlanDraftMutation,
    useRestoreTrackingPlanVersionMutation,
    useGetTrackingPlanTimelineQuery,
    useLazyGetTrackingPlanTimelineQuery,
    useGetTrackingPlanImplementationPlanQuery,
    useLazyGetTrackingPlanImplementationPlanQuery,
    useSendTrackingPlanImplementationPlanMutation,
    useGetTrackingPlanTagManagerQuery,
    useGetTrackingPlanStatusQuery,
    useRefreshTrackingPlanMutation,
    useGetTrackingPlanKnownVendorsQuery,
    usePrepareTagManagerChangeSetsMutation,
    useDiscardTagManagerChangeSetMutation,
    usePublishTagManagerChangeSetMutation,
    useRestoreTagManagerEntitiesMutation,
    useGetOauthAuthorizationRequestQuery,
    useApproveOauthAuthorizationRequestMutation,
    useDenyOauthAuthorizationRequestMutation,
    useGetOauthConnectionsQuery,
    useDisconnectOauthConnectionMutation,
    useGetOrganizationOauthConnectionsQuery,
    useDisconnectOrganizationOauthConnectionMutation,
    useGetOauthPolicyQuery,
    useUpdateOauthPolicyMutation,
} = api;

/**
 * The tracking plan and its draft events.
 *
 * One tag rather than one per event: every write here changes the draft as a
 * whole — an added event moves nothing else, but a reorder moves all of them,
 * and the screen reads the plan in one request anyway.
 */
function getTrackingPlanPaths(builder: ApiEndpointBuilder) {
    return {
        /**
         * 404 until somebody creates the plan, which is the empty state rather
         * than an error — see `Plan.tsx`.
         */
        getTrackingPlan: builder.query<TrackingPlan, undefined>({
            query: () => ({
                url: `tracking-plan`,
                method: "GET",
            }),
            // A workspace with no plan is the screen's empty state, not a
            // failure worth a toast.
            extraOptions: { expectedStatuses: [404] },
            providesTags: [{ type: "TrackingPlan", id: "PLAN" }],
        }),
        saveTrackingPlanVendor: builder.mutation<
            void,
            Omit<TrackingPlan["vendors"][number], "id" | "eventNameVariable">
        >({
            extraOptions: { errorToast: false },
            query: (body) => ({
                url: "tracking-plan/vendors",
                method: "PUT",
                body,
            }),
            invalidatesTags: [
                { type: "TrackingPlan", id: "PLAN" },
                { type: "TrackingPlanTimeline", id: "LIST" },
            ],
        }),
        /** The site tools, the whole list, saved into the draft; each brings its vendor along. */
        saveTrackingPlanSiteTools: builder.mutation<
            void,
            { siteTools: TrackingPlan["siteTools"] }
        >({
            extraOptions: { errorToast: false },
            query: (body) => ({
                url: "tracking-plan/site-tools",
                method: "PUT",
                body,
            }),
            invalidatesTags: [
                { type: "TrackingPlan", id: "PLAN" },
                { type: "TrackingPlanTimeline", id: "LIST" },
            ],
        }),
        /** A rule's level, saved into the draft; checked once promoted. */
        saveTrackingPlanPolicyRule: builder.mutation<
            void,
            { key: PolicyRuleKey; level: RuleLevel }
        >({
            extraOptions: { errorToast: false },
            query: ({ key, level }) => ({
                url: `tracking-plan/policy-rules/${encodeURIComponent(key)}`,
                method: "PUT",
                body: { level },
            }),
            invalidatesTags: [
                { type: "TrackingPlan", id: "PLAN" },
                { type: "TrackingPlanTimeline", id: "LIST" },
            ],
        }),
        /**
         * A person's word for a tag we could not identify: the vendor it sends
         * to, or null for none. The plan is evaluated again before this
         * answers, so the status read after it already says so.
         */
        confirmTrackingPlanTag: builder.mutation<
            void,
            {
                containerPublicId: string;
                tagId: string;
                vendorKey: string | null;
            }
        >({
            extraOptions: { errorToast: false },
            query: (body) => ({
                url: "tracking-plan/tag-confirmations",
                method: "PUT",
                body,
            }),
            invalidatesTags: [
                { type: "TrackingPlan", id: "STATUS" },
                { type: "TrackingPlanTimeline", id: "LIST" },
            ],
        }),
        removeTrackingPlanTagConfirmation: builder.mutation<
            void,
            { containerPublicId: string; tagId: string }
        >({
            extraOptions: { errorToast: false },
            query: ({ containerPublicId, tagId }) => ({
                url: `tracking-plan/tag-confirmations/${encodeURIComponent(containerPublicId)}/${encodeURIComponent(tagId)}`,
                method: "DELETE",
            }),
            invalidatesTags: [
                { type: "TrackingPlan", id: "STATUS" },
                { type: "TrackingPlanTimeline", id: "LIST" },
            ],
        }),
        removeTrackingPlanVendor: builder.mutation<void, string>({
            extraOptions: { errorToast: false },
            query: (key) => ({
                url: `tracking-plan/vendors/${encodeURIComponent(key)}`,
                method: "DELETE",
            }),
            invalidatesTags: [
                { type: "TrackingPlan", id: "PLAN" },
                { type: "TrackingPlanTimeline", id: "LIST" },
            ],
        }),
        getTaggingCollection: builder.query<TaggingCollectionSetup, undefined>({
            query: () => ({ url: "tagging-container/collection" }),
            providesTags: [{ type: "TrackingPlan", id: "SETUP" }],
        }),
        createTrackingPlan: builder.mutation<TrackingPlan, undefined>({
            query: () => ({
                url: `tracking-plan`,
                method: "POST",
            }),
            // The first action opens the event editor, which needs the plan immediately.
            async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(
                        api.util.upsertQueryEntries([
                            {
                                endpointName: "getTrackingPlan",
                                arg: undefined,
                                value: data,
                            },
                        ]),
                    );
                } catch {
                    // The base query reports the failure.
                }
            },
            invalidatesTags: [
                { type: "TrackingPlan", id: "PLAN" },
                { type: "TrackingPlanTimeline", id: "LIST" },
            ],
        }),
        createTrackingPlanEvent: builder.mutation<
            TrackingPlanEvent,
            TrackingPlanEventDraft
        >({
            extraOptions: { errorToast: false },
            query: (event) => ({
                url: `tracking-plan/events`,
                method: "POST",
                body: event,
            }),
            // As for an update: the editor opens the new event's page straight away.
            async onQueryStarted(_event, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(
                        api.util.updateQueryData(
                            "getTrackingPlan",
                            undefined,
                            (draft) => {
                                if (
                                    !draft.events.some(
                                        (row) => row.id === data.id,
                                    )
                                )
                                    draft.events.push(data);
                            },
                        ),
                    );
                } catch {
                    // The failure is the mutation's to report.
                }
            },
            invalidatesTags: [
                { type: "TrackingPlan", id: "PLAN" },
                { type: "TrackingPlanTimeline", id: "LIST" },
            ],
        }),
        /** A Patch: only what is sent changes, so a rename is `{ id, name }`. */
        updateTrackingPlanEvent: builder.mutation<
            TrackingPlanEvent,
            Partial<TrackingPlanEventDraft> & { id: string }
        >({
            extraOptions: { errorToast: false },
            query: ({ id, ...event }) => ({
                url: `tracking-plan/events/${id}`,
                method: "PATCH",
                body: event,
            }),
            // The editor goes straight to the event's page, which reads the plan: put
            // the saved row in now, or the page shows the event as it was until the
            // plan's refetch lands, which with evaluation behind it takes seconds.
            async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(
                        api.util.updateQueryData(
                            "getTrackingPlan",
                            undefined,
                            (draft) => {
                                const index = draft.events.findIndex(
                                    (row) => row.id === id,
                                );
                                if (index >= 0) draft.events[index] = data;
                            },
                        ),
                    );
                } catch {
                    // The failure is the mutation's to report.
                }
            },
            invalidatesTags: [
                { type: "TrackingPlan", id: "PLAN" },
                { type: "TrackingPlan", id: "STATUS" },
                "Workspaces",
                { type: "TrackingPlanTimeline", id: "LIST" },
            ],
        }),
        deleteTrackingPlanEvent: builder.mutation<void, string>({
            query: (id) => ({
                url: `tracking-plan/events/${id}`,
                method: "DELETE",
            }),
            // Off the table at once, and back if the Api refuses.
            async onQueryStarted(id, { dispatch, queryFulfilled }) {
                const patch = dispatch(
                    api.util.updateQueryData(
                        "getTrackingPlan",
                        undefined,
                        (draft) => {
                            draft.events = draft.events.filter(
                                (row) => row.id !== id,
                            );
                        },
                    ),
                );
                try {
                    await queryFulfilled;
                } catch {
                    patch.undo();
                }
            },
            invalidatesTags: [
                { type: "TrackingPlan", id: "PLAN" },
                { type: "TrackingPlanTimeline", id: "LIST" },
            ],
        }),
        /** Every event on the plan, once, in the order the table now shows. */
        reorderTrackingPlanEvents: builder.mutation<void, string[]>({
            query: (ids) => ({
                url: `tracking-plan/events:reorder`,
                method: "PATCH",
                body: { ids },
            }),
            // The table already shows the new order the moment a row is
            // dropped, so the cache has to agree straight away -- otherwise the
            // row snaps back to where it was until the refetch lands, which
            // reads as the drag having failed.
            async onQueryStarted(ids, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    api.util.updateQueryData(
                        "getTrackingPlan",
                        undefined,
                        (draft) => {
                            draft.events = ids
                                .map((id) =>
                                    draft.events.find(
                                        (event) => event.id === id,
                                    ),
                                )
                                .filter((event) => event !== undefined);
                        },
                    ),
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
            invalidatesTags: [
                { type: "TrackingPlan", id: "PLAN" },
                { type: "TrackingPlanTimeline", id: "LIST" },
            ],
        }),
        /** Every promoted version, newest first, each with what it changed. */
        getTrackingPlanVersions: builder.query<
            TrackingPlanVersion[],
            undefined
        >({
            query: () => ({
                url: `tracking-plan/versions`,
                method: "GET",
            }),
            providesTags: [{ type: "TrackingPlanVersions", id: "LIST" }],
        }),
        /** One version with the plan as it promoted it. */
        getTrackingPlanVersion: builder.query<
            TrackingPlanVersionDetail,
            number
        >({
            query: (number) => ({
                url: `tracking-plan/versions/${number}`,
                method: "GET",
            }),
            providesTags: (_result, _error, number) => [
                { type: "TrackingPlanVersions", id: number },
            ],
        }),
        /**
         * What lies between two versions. Without `against`, the version
         * before -- which is what the version already carries.
         */
        getTrackingPlanVersionDiff: builder.query<
            TrackingPlanVersionDiff,
            { number: number; against?: number }
        >({
            query: ({ number, against }) => ({
                url: `tracking-plan/versions/${number}/diff`,
                method: "GET",
                params: against === undefined ? undefined : { against },
            }),
            providesTags: [{ type: "TrackingPlanVersions", id: "LIST" }],
        }),
        /**
         * What happened to the plan, newest first, a page at a time: `cursor`
         * is the `nextCursor` of the page before, `kind` a comma-separated
         * narrowing, `since` an instant nothing older is wanted from, `limit`
         * the page size (200 at most). Every write to the plan invalidates
         * it, since every write is an entry.
         */
        getTrackingPlanTimeline: builder.query<
            TrackingPlanTimeline,
            { kind?: string; cursor?: string; since?: string; limit?: number }
        >({
            query: ({ kind, cursor, since, limit }) => ({
                url: `tracking-plan/timeline`,
                method: "GET",
                params: {
                    ...(kind === undefined ? {} : { kind }),
                    ...(cursor === undefined ? {} : { cursor }),
                    ...(since === undefined ? {} : { since }),
                    ...(limit === undefined ? {} : { limit }),
                },
            }),
            // Only the head page follows writes: the older pages are keyed
            // off where it ends and start over when it moves, so refetching
            // them on a write would be work the page throws away.
            providesTags: (_result, _error, { cursor }) =>
                cursor === undefined
                    ? [{ type: "TrackingPlanTimeline", id: "LIST" }]
                    : [],
        }),
        /**
         * Promote the draft: the rows become the next numbered version and
         * monitoring follows it. The plan is refetched because its live
         * version and its draft both change.
         */
        promoteTrackingPlan: builder.mutation<
            TrackingPlanVersionDetail,
            { name: string; notes?: string }
        >({
            extraOptions: { errorToast: false },
            query: (body) => ({
                url: `tracking-plan/versions`,
                method: "POST",
                body,
            }),
            // Promoting writes the verdicts for the new version at once, so
            // the header's counts follow the version number it shows.
            invalidatesTags: [
                { type: "TrackingPlan", id: "PLAN" },
                { type: "TrackingPlan", id: "STATUS" },
                { type: "TrackingPlanVersions", id: "LIST" },
                { type: "TrackingPlanTimeline", id: "LIST" },
            ],
        }),
        /**
         * The developer's brief, rendered: the page a shared link opens, for
         * the dialog's preview, or the same as Markdown for a ticket. Provided
         * under the plan's tag, since it is the plan read another way.
         */
        getTrackingPlanImplementationPlan: builder.query<
            TrackingPlanImplementationPlan,
            { event?: string; format?: "html" | "md" }
        >({
            query: ({ event, format }) => ({
                url: `tracking-plan/implementation-plan`,
                params: {
                    ...(event === undefined ? {} : { event }),
                    ...(format === undefined ? {} : { format }),
                },
            }),
            providesTags: [{ type: "TrackingPlan", id: "PLAN" }],
        }),
        /**
         * Mail each developer their own link to the brief. Nothing but the
         * addresses reaches the mail; the plan is what the link opens.
         */
        sendTrackingPlanImplementationPlan: builder.mutation<
            void,
            { recipients: string[]; event?: string }
        >({
            extraOptions: { errorToast: false },
            query: (body) => ({
                url: `tracking-plan/implementation-plan:send`,
                method: "POST",
                body,
            }),
            invalidatesTags: [{ type: "TrackingPlanTimeline", id: "LIST" }],
        }),
        /** Throw the draft away: the rows are rewritten from the live version. */
        discardTrackingPlanDraft: builder.mutation<TrackingPlan, undefined>({
            query: () => ({
                url: `tracking-plan/draft`,
                method: "DELETE",
            }),
            invalidatesTags: [
                { type: "TrackingPlan", id: "PLAN" },
                { type: "TrackingPlanTimeline", id: "LIST" },
            ],
        }),
        /** Reopen an older version as the draft; nothing is promoted. */
        restoreTrackingPlanVersion: builder.mutation<TrackingPlan, number>({
            query: (number) => ({
                url: `tracking-plan/versions/${number}:restore`,
                method: "POST",
            }),
            invalidatesTags: [
                { type: "TrackingPlan", id: "PLAN" },
                { type: "TrackingPlanTimeline", id: "LIST" },
            ],
        }),
        /**
         * What the plan writes into Tag Manager, per container: who we reach
         * it as, and the change set it holds, entity by entity. Read from our
         * own records, so polling it while a set is written costs Google's
         * quota nothing.
         */
        getTrackingPlanTagManager: builder.query<
            TrackingPlanTagManager,
            undefined
        >({
            query: () => ({ url: `tracking-plan/tag-manager` }),
            // The Api binds the plan to its pair when it can, this read
            // included. A pair the cached plan does not know about means it
            // just happened, so what hangs off the pair is read again.
            async onQueryStarted(_, { dispatch, getState, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    const plan =
                        api.endpoints.getTrackingPlan.select(undefined)(
                            getState(),
                        ).data;
                    if (
                        data.containers.length === 2 &&
                        plan &&
                        !plan.webContainerPublicId
                    ) {
                        dispatch(
                            api.util.invalidateTags([
                                { type: "TrackingPlan", id: "PLAN" },
                                { type: "TrackingPlan", id: "STATUS" },
                                { type: "TrackingPlanTimeline", id: "LIST" },
                            ]),
                        );
                    }
                } catch {
                    // The query's own error state says it.
                }
            },
            providesTags: [
                { type: "TagManagerChangeSets", id: "LIST" },
                { type: "WorkspaceIntegrations", id: "LIST" },
            ],
        }),
        /**
         * The vendors whose tags can be recognised without a person
         * confirming them, and the key a plan's vendor must use for that: what
         * Add vendor offers. The same for every workspace, and never stale
         * within a session.
         */
        getTrackingPlanKnownVendors: builder.query<
            TrackingPlanKnownVendor[],
            undefined
        >({
            query: () => ({ url: `tracking-plan/known-vendors` }),
            keepUnusedDataFor: 3600,
        }),
        /**
         * The stored verdict per promoted event — status, reason, where it
         * stands per container, and the restores that would put it right.
         * Written hourly by the evaluator, on every container read and on
         * every promotion; read from our own rows, so the plan screen asks on
         * every load and polls while it is open (Plan.tsx).
         */
        getTrackingPlanStatus: builder.query<
            TrackingPlanStatusReport,
            undefined
        >({
            query: () => ({ url: `tracking-plan/status` }),
            // Asked before the screen knows there is a plan; no plan is the
            // empty state, not a failure worth a toast.
            extraOptions: { expectedStatuses: [404] },
            providesTags: [{ type: "TrackingPlan", id: "STATUS" }],
        }),
        /**
         * The hourly passes for this plan, now: both containers are read from
         * Tag Manager, arrivals are fetched and the plan is judged from both.
         * Answers with the status as it now stands. Limited per plan — a 429
         * says when it can be asked again, and the button that calls this
         * says so itself rather than toasting.
         */
        refreshTrackingPlan: builder.mutation<
            TrackingPlanStatusReport,
            undefined
        >({
            extraOptions: { expectedStatuses: [429] },
            query: () => ({ url: `tracking-plan/refresh`, method: "POST" }),
            invalidatesTags: [
                { type: "TrackingPlan", id: "STATUS" },
                { type: "TagManagerChangeSets", id: "LIST" },
                { type: "TrackingPlanTimeline", id: "LIST" },
            ],
        }),
        /**
         * Queue the live version, or a named one, for writing into the
         * containers — and, with `publish`, for publishing once written. The
         * writing happens in the background; the tab polls.
         */
        prepareTagManagerChangeSets: builder.mutation<
            TrackingPlanChangeSets,
            { versionNumber?: number; publish?: boolean; replace?: boolean }
        >({
            extraOptions: { errorToast: false },
            query: (body) => ({
                url: `tracking-plan/tag-manager/change-sets`,
                method: "POST",
                body,
            }),
            invalidatesTags: [{ type: "TagManagerChangeSets", id: "LIST" }],
        }),
        /**
         * Discard a set that is not published: taken back out of our
         * workspace in the background, or at once when it never reached it.
         * The timeline records the discard once it is done.
         */
        discardTagManagerChangeSet: builder.mutation<
            TagManagerChangeSetDetail,
            { id: string }
        >({
            extraOptions: { errorToast: false },
            query: ({ id }) => ({
                url: `tracking-plan/tag-manager/change-sets/${id}`,
                method: "DELETE",
            }),
            invalidatesTags: [
                { type: "TagManagerChangeSets", id: "LIST" },
                { type: "TrackingPlanTimeline", id: "LIST" },
            ],
        }),
        /**
         * Publish a written set: a Tag Manager version of our workspace, made
         * and published in the background. Needs a connection that allows
         * publishing; the tab polls until the set is seen live.
         */
        publishTagManagerChangeSet: builder.mutation<
            TagManagerChangeSetDetail,
            { id: string }
        >({
            extraOptions: { errorToast: false },
            query: ({ id }) => ({
                url: `tracking-plan/tag-manager/change-sets/${id}:publish`,
                method: "POST",
            }),
            invalidatesTags: [{ type: "TagManagerChangeSets", id: "LIST" }],
        }),
        /**
         * Put entities of ours back as the plan published them: the fix a
         * drift finding offers. One change set of origin `restore` for the
         * container, written in the background like any other; with
         * `publish`, published once written. Only drift findings can be
         * named, by kind and id.
         */
        restoreTagManagerEntities: builder.mutation<
            TagManagerChangeSetDetail,
            {
                publicId: string;
                entities: { kind: SnapshotEntityKind; entityId: string }[];
                publish?: boolean;
            }
        >({
            extraOptions: { errorToast: false },
            query: ({ publicId, ...body }) => ({
                url: `tracking-plan/tag-manager/containers/${publicId}:restore`,
                method: "POST",
                body,
            }),
            // The tab reads the set from its container. Written into the
            // cache on the answer, so Restore is not offered again in the
            // moment before the refetch lands.
            async onQueryStarted({ publicId }, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(
                        api.util.updateQueryData(
                            "getTrackingPlanTagManager",
                            undefined,
                            (draft) => {
                                const container = draft.containers.find(
                                    (candidate) =>
                                        candidate.publicId === publicId,
                                );
                                if (container) container.changeSet = data;
                            },
                        ),
                    );
                } catch {
                    // The error is the caller's to show; the cache is as it was.
                }
            },
            invalidatesTags: [{ type: "TagManagerChangeSets", id: "LIST" }],
        }),
    };
}

/**
 * Connecting apps such as Claude and ChatGPT (specs/chat-ga/plan.md, "Mcp"):
 * the consent screen, "Connected apps", and the organization's policy.
 */
function getOauthPaths(builder: ApiEndpointBuilder) {
    return {
        /** 404 once the request expired or was answered; the screen says so. */
        getOauthAuthorizationRequest: builder.query<
            OauthConsentRequest,
            { id: string }
        >({
            query: ({ id }) => ({ url: `oauth/authorization-requests/${id}` }),
            extraOptions: { expectedStatuses: [404] },
        }),
        approveOauthAuthorizationRequest: builder.mutation<
            OauthConsentDecision,
            {
                id: string;
            } & paths["/oauth/authorization-requests/{id}/approve"]["post"]["requestBody"]["content"]["application/json"]
        >({
            query: ({ id, ...body }) => ({
                url: `oauth/authorization-requests/${id}/approve`,
                method: "POST",
                body,
            }),
            extraOptions: { errorToast: false },
            invalidatesTags: [{ type: "OauthConnections", id: "LIST" }],
        }),
        denyOauthAuthorizationRequest: builder.mutation<
            OauthConsentDecision,
            { id: string }
        >({
            query: ({ id }) => ({
                url: `oauth/authorization-requests/${id}/deny`,
                method: "POST",
            }),
            extraOptions: { errorToast: false },
        }),
        getOauthConnections: builder.query<OauthConnection[], void>({
            query: () => ({ url: `user/oauth-connections` }),
            providesTags: [{ type: "OauthConnections", id: "LIST" }],
        }),
        disconnectOauthConnection: builder.mutation<void, { id: string }>({
            query: ({ id }) => ({
                url: `user/oauth-connections/${id}`,
                method: "DELETE",
            }),
            async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    api.util.updateQueryData(
                        "getOauthConnections",
                        undefined,
                        (draft) =>
                            draft.filter((connection) => connection.id !== id),
                    ),
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
            invalidatesTags: [
                { type: "OauthConnections", id: "LIST" },
                { type: "OauthPolicy", id: "LIST" },
            ],
        }),
        getOrganizationOauthConnections: builder.query<
            OauthOrganizationConnection[],
            void
        >({
            query: () => ({ url: `user/organization/oauth-connections` }),
            providesTags: [{ type: "OauthConnections", id: "LIST" }],
        }),
        disconnectOrganizationOauthConnection: builder.mutation<
            void,
            { id: string }
        >({
            query: ({ id }) => ({
                url: `user/organization/oauth-connections/${id}`,
                method: "DELETE",
            }),
            async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    api.util.updateQueryData(
                        "getOrganizationOauthConnections",
                        undefined,
                        (draft) =>
                            draft.filter((connection) => connection.id !== id),
                    ),
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
            invalidatesTags: [
                { type: "OauthConnections", id: "LIST" },
                { type: "OauthPolicy", id: "LIST" },
            ],
        }),
        getOauthPolicy: builder.query<
            OauthPolicySettings,
            { audience: OauthAudience }
        >({
            query: ({ audience }) => ({
                url: `user/organization/oauth-policies/${audience}`,
            }),
            providesTags: [{ type: "OauthPolicy", id: "LIST" }],
        }),
        /** Saving applies at once: connections the policy refuses end. */
        updateOauthPolicy: builder.mutation<
            OauthPolicySettings,
            {
                audience: OauthAudience;
            } & paths["/user/organization/oauth-policies/{audience}"]["put"]["requestBody"]["content"]["application/json"]
        >({
            query: ({ audience, ...body }) => ({
                url: `user/organization/oauth-policies/${audience}`,
                method: "PUT",
                body,
            }),
            async onQueryStarted(
                { audience, ...policy },
                { dispatch, queryFulfilled },
            ) {
                const patchResult = dispatch(
                    api.util.updateQueryData(
                        "getOauthPolicy",
                        { audience },
                        (draft) => ({ ...draft, ...policy }),
                    ),
                );
                try {
                    const { data } = await queryFulfilled;
                    dispatch(
                        api.util.updateQueryData(
                            "getOauthPolicy",
                            { audience },
                            () => data,
                        ),
                    );
                } catch {
                    patchResult.undo();
                }
            },
            invalidatesTags: [
                { type: "OauthConnections", id: "LIST" },
                { type: "OauthPolicy", id: "LIST" },
            ],
        }),
    };
}

/**
 * A version with its content: the plan as it promoted it. The list carries
 * versions without it, since the rows are the content now.
 */
type TrackingPlanVersionDetail =
    paths["/tracking-plan/versions/{number}"]["get"]["responses"]["200"]["content"]["application/json"];
